import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2'; // يفضل استخدامه لرسائل التأكيد الاحترافية
import api from './axiosConfig';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { 
  Save, Search, Box, Plus, ShieldAlert, Activity, FileText, GitBranch, List, Filter, Download, Maximize2, Calendar, Settings, TrendingUp, Loader2,   
  History, MapPin, Trash2, Camera, ChevronRight, Globe, DollarSign, Upload, RefreshCw, ArrowLeft, ChevronLeft, Minus, Type, Paperclip, Tag, BrainCircuit, Brain, Wrench    
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import StatusModal  from './StatusModal';
import StatusHistoryModal  from './StatusHistoryModal';
import UniversalLookup from './UniversalLookup';
import ClassificationTree from './ClassificationTree';
import EnterMeterReadings from './EnterMeterReadings';
import MeterHistoryModal from './MeterHistoryModal';
import ReportDowntimeModal from './ReportDowntimeModal';
import DowntimeHistoryModal from './DowntimeHistoryModal';
import MoveAssetModal from './MoveAssetModal';
import MoveHistoryModal from './MoveHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal'
import SmartPrintButton from './PrintButton'; 
import IncidentModal from './IncidentModal';
import IncidentHistoryModal from './IncidentHistoryModal';
import { useFontSize } from './FontSizeContext'; 
import PredictiveChart from './PredictiveChart';



// --- BIM (Autodesk Viewer) ---
  const BIMViewer = ({ urn, activeGuid, onAssetClick, assetStatuses, assetData, workHistory }) => {
  const viewerContainer = useRef(null);
  const viewer = useRef(null);
  
  const isInitializing = useRef(false);
  const bubbleDataRef = useRef(null);
  const [isTreeReady, setIsTreeReady] = useState(false);

  // 1. التهيئة الأساسية للموديل
  useEffect(() => {
    if (!urn || viewer.current || isInitializing.current) return;
    isInitializing.current = true;
    const Autodesk = window.Autodesk;

    const options = {
      env: 'AutodeskProduction2',
      api: 'streamingV2',
      getAccessToken: async (callback) => {
        try {
          const res = await axios.get('/api/bim/token');
          callback(res.data.access_token, res.data.expires_in);
        } catch (error) {
          console.error("Failed to fetch APS Token", error);
        }
      }
    };

    Autodesk.Viewing.Initializer(options, () => {
      viewer.current = new Autodesk.Viewing.GuiViewer3D(viewerContainer.current);
      viewer.current.start();
      
      const documentId = 'urn:' + urn;
      Autodesk.Viewing.Document.load(documentId, (doc) => {
        const viewables = doc.getRoot().getDefaultGeometry();
        
        viewer.current.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => {
            console.log("✅ شجرة البيانات اكتملت. الموديل جاهز للتفاعل!");
            setIsTreeReady(true); 
        });

        viewer.current.loadDocumentNode(doc, viewables).then(() => {
          viewer.current.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event) => {
            const dbId = event.dbIdArray[0];
            if (dbId && onAssetClick) {
              viewer.current.getProperties(dbId, (props) => {
                onAssetClick({ guid: props.externalId, dbId: dbId, name: props.name });
              });
            }
          });
        });
      });
    });

    return () => {
      if (viewer.current) {
        viewer.current.finish();
        viewer.current = null;
        isInitializing.current = false;
      }
    };
  }, [urn, onAssetClick]);

  // 2. الزووم عند التحديد من النظام 
  useEffect(() => {
    if (!isTreeReady || !viewer.current || !viewer.current.model || !activeGuid || !window.THREE) return;
    
    const v = viewer.current;
    const model = v.model;

    const handleFocus = (dbId) => {
      v.fitToView([dbId]); 
      v.select([dbId]);    
      setTimeout(() => v.clearSelection(), 2000); 
    };

    model.getExternalIdMapping((mapping) => {
      const dbId = mapping[activeGuid];
      if (dbId) {
        handleFocus(dbId);
      } else {
        v.search(activeGuid, (dbIds) => {
          if (dbIds && dbIds.length > 0) handleFocus(dbIds[0]);
        }, (err) => console.error(err), ['externalId', 'name', 'label']);
      }
    });
  }, [activeGuid, isTreeReady]);

  // 3. التلوين الديناميكي اللحظي (Live Theming)
  useEffect(() => {
    if (!isTreeReady || !viewer.current || !viewer.current.model || !assetStatuses || !window.THREE) return;
    
    const v = viewer.current;
    const THREE = window.THREE;

    v.clearThemingColors(v.model);

    v.model.getExternalIdMapping((mapping) => {
        assetStatuses.forEach(assetStatus => {
            const dbId = mapping[assetStatus.guid];
            if (dbId) {
                let color;
                switch(assetStatus.status) {
                    case 'BROKEN': color = new THREE.Vector4(1, 0, 0, 0.7); break; 
                    case 'DECOMMISSIONED': color = new THREE.Vector4(0.2, 0.2, 0.2, 0.8); break; 
                    case 'INACTIVE': color = new THREE.Vector4(0.6, 0.6, 0.6, 0.7); break; 
                    case 'MISSING': color = new THREE.Vector4(0.5, 0, 0.5, 0.7); break; 
                    case 'SEALED': color = new THREE.Vector4(0.6, 0.3, 0, 0.7); break; 
                    case 'NOT READY': color = new THREE.Vector4(1, 0.6, 0, 0.7); break; 
                    case 'ACTIVE': color = new THREE.Vector4(0, 0.8, 0.8, 0.7); break; 
                    case 'LIMITED USE': color = new THREE.Vector4(0, 0, 1, 0.7); break; 
                    case 'OPERATING': color = new THREE.Vector4(0, 1, 0, 0.7); break; 
                    default: return;
                }
                v.setThemingColor(dbId, color);
            }
        });
    });
  }, [assetStatuses, isTreeReady]);

// =================================================================
  // 4. دالة رسم البابل (تم إصلاح الـ undefined واللغة العربية)
  // =================================================================
  const renderBubble = useCallback(() => {
    if (!bubbleDataRef.current || !viewer.current ) return;
    
    const { data, center3D } = bubbleDataRef.current;
    const screenPoint = viewer.current.worldToClient(center3D);
    
    if (!screenPoint) return;

    let bubble = document.getElementById(`bubble-${data.guid}`);
    if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = `bubble-${data.guid}`;
        viewer.current.container.appendChild(bubble);
    }

    bubble.style.position = 'absolute';
    bubble.style.zIndex = '999999'; 
    bubble.style.left = `${screenPoint.x}px`;
    bubble.style.top = `${screenPoint.y - 20}px`; 
    bubble.style.transform = 'translate(-50%, -100%)'; 
    bubble.style.pointerEvents = 'none'; 
    bubble.style.transition = 'none'; 
    
    const borderColor = data.status === 'BROKEN' ? '#dc2626' : '#22c55e'; 
    bubble.style.borderLeft = `5px solid ${borderColor}`;
    bubble.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
    bubble.style.backdropFilter = 'blur(4px)';
    bubble.style.padding = '12px';
    bubble.style.borderRadius = '8px';
    bubble.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
    bubble.style.minWidth = '160px';
    bubble.style.fontFamily = 'sans-serif';

    if (screenPoint.z > 1) {
        bubble.style.display = 'none';
        return;
    } else {
        bubble.style.display = 'block';
    }

    const totalWo = data.stats?.total_wo || 0;
    const hasActiveWo = data.stats?.has_active_wo || false;
    const activeWoNum = data.stats?.active_wo_num || '';
    
    // 🎯 إصلاح الـ Undefined: استخدام meter_name و last_reading، وفرض LTR
    const metersHtml = (data.meters || []).map(m => `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;" dir="ltr">
            <span style="font-size: 10px; color: #6b7280; font-weight: bold;">${m.meter_name || 'Meter'}:</span>
            <span style="font-size: 11px; font-weight: 900; color: ${(m.last_reading || 0) > 50 ? '#dc2626' : '#16a34a'}">
                ${m.last_reading || 0} <span style="font-size: 8px; color: #9ca3af;">${m.unit_of_measure || ''}</span>
            </span>
        </div>
    `).join('');

    // 🎯 إضافة dir="ltr" لكل الحاويات عشان العربي مايبوظش الشكل
    bubble.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 6px;" dir="ltr">
            <span style="font-size: 12px; font-weight: 900; color: #1e293b; letter-spacing: 0.5px;">${data.assetnum}</span>
            <span style="font-size: 9px; padding: 2px 6px; background-color: #dbeafe; color: #1e40af; border-radius: 4px; font-weight: bold; animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;">LIVE</span>
        </div>
        ${metersHtml}
        <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; display: flex; flex-direction: column; gap: 4px;" dir="ltr">
            <div style="display: flex; justify-content: space-between; font-size: 10px;">
                <span style="color: #9ca3af; font-weight: bold;">Total WOs:</span>
                <span style="font-weight: 900; color: #334155;">${totalWo}</span>
            </div>
            ${hasActiveWo ? `
                <div style="display: flex; align-items: center; gap: 4px; font-size: 10px; color: #ea580c; font-weight: 900; margin-top: 2px;">
                    <span style="width: 6px; height: 6px; background-color: #ea580c; border-radius: 50%;"></span>
                    Active WO: ${activeWoNum}
                </div>
            ` : '<div style="font-size: 10px; color: #9ca3af; font-style: italic; margin-top: 2px; text-align: left;">No active maintenance</div>'}
        </div>
    `;
  }, []);

  // =================================================================
  // 5. 🎯 السحر الجديد: الظهور التلقائي بمجرد فتح الموديل
  // =================================================================
  useEffect(() => {
    if (!isTreeReady || !viewer.current || !activeGuid || !window.THREE || !assetData) return;

    // بنستنى ثانية ونص لحد ما الكاميرا تعمل زووم وبعدين نضرب البابل
    const autoBubbleTimer = setTimeout(() => {
        const activeWOs = (workHistory || []).filter(wo => ['INPRG', 'WAPPR'].includes(wo.status));
        
        const initialPayload = {
            guid: activeGuid,
            assetnum: assetData.assetnum || '---',
            status: assetData.status || 'OPERATING',
            meters: assetData.meters || [],
            stats: {
                total_wo: workHistory?.length || 0,
                has_active_wo: activeWOs.length > 0,
                active_wo_num: activeWOs.length > 0 ? activeWOs[0].wonum : null
            }
        };

        console.log("🚀 جاري حقن بيانات البابل أوتوماتيكياً...", initialPayload);
        window.dispatchEvent(new CustomEvent('showAssetBubble', { detail: initialPayload }));

    }, 1500); 

    return () => clearTimeout(autoBubbleTimer);
  }, [isTreeReady, activeGuid, assetData, workHistory]);

  // =================================================================
  // 6. مراقب الكاميرا (لالتصاق البابل)
  // =================================================================
  useEffect(() => {
    if (!isTreeReady || !viewer.current) return;
    
    const onCameraChange = () => {
        if (bubbleDataRef.current) renderBubble();
    };

    viewer.current.addEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
    
    return () => {
        if (viewer.current) {
            viewer.current.removeEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
        }
    };
  }, [isTreeReady, renderBubble]);

  // =================================================================
  // 7. الاستماع لإشارة السوكيت (باستخدام الطريقة الأكيدة والدمج الذكي)
  // =================================================================
  useEffect(() => {
    if (!isTreeReady || !viewer.current || !window.THREE) return;
    
    const handleShowBubble = (e) => {
        const newData = e.detail;
        const v = viewer.current;
        console.log("🫧 أمر رسم البابل وصل للأصل:", newData.assetnum);
        
        v.model.getExternalIdMapping((mapping) => {
            const dbId = mapping[newData.guid];
            if (!dbId) {
                console.warn("⚠️ الأصل مش موجود في الفهرس المباشر للبابل!");
                return;
            }

            const tree = v.model.getInstanceTree();
            const fragList = v.model.getFragmentList();
            const bbox = new window.THREE.Box3();

            tree.enumNodeFragments(dbId, (fragId) => {
                const fragBox = new window.THREE.Box3();
                fragList.getWorldBounds(fragId, fragBox);
                bbox.union(fragBox);
            }, true);

            const center3D = new window.THREE.Vector3(
                (bbox.min.x + bbox.max.x) / 2,
                (bbox.min.y + bbox.max.y) / 2,
                bbox.max.z 
            );

            // 🎯 الدمج الذكي: نحتفظ بالبيانات القديمة ونحدثها بالجديدة فقط
            const currentData = bubbleDataRef.current?.data || {};
            const mergedData = { ...currentData, ...newData };

            bubbleDataRef.current = { data: mergedData, center3D };
            renderBubble(); 
        });
    };

    window.addEventListener('showAssetBubble', handleShowBubble);
    return () => {
        window.removeEventListener('showAssetBubble', handleShowBubble);
        if (bubbleDataRef.current) {
            const b = document.getElementById(`bubble-${bubbleDataRef.current.data.guid}`);
            if (b) b.remove();
            bubbleDataRef.current = null;
        }
    };
  }, [isTreeReady, renderBubble]);

  if (!urn) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500">
      <ShieldAlert size={48} className="mb-4 opacity-20" />
      <p className="font-bold uppercase tracking-widest">No 3D Model Linked to this Asset</p>
    </div>
  );

  return (
      <div className="w-full h-full relative">
          {!isTreeReady && urn && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm text-blue-400">
                  <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest animate-pulse">Building Digital Twin Data...</span>
              </div>
          )}
          <div ref={viewerContainer} className="w-full h-full rounded-xl overflow-hidden" />
      </div>
  );
};

  // 🎯 التعديل: ضفنا bim_id و status و onOpen3D في الخواص (Props)
// 🎯 التعديل: استقبال t و isRTL للترجمة والاتجاه
  const MapView = ({ lat, lng, assetnum, bim_id, status, onLocationChange, onOpen3D, t, isRTL }) => {
    const markerRef = useRef(null);
    const center = [lat || 30.0444, lng || 31.2357];
  
    const eventHandlers = useMemo(
      () => ({
        dragend() {
          const marker = markerRef.current;
          if (marker != null) {
            const newPos = marker.getLatLng();
            onLocationChange(newPos.lat, newPos.lng);
          }
        },
      }),
      [onLocationChange],
    );
  
    const { LayersControl, TileLayer, Marker, Popup } = require('react-leaflet');
    const L = require('leaflet'); 
    const { Globe } = require('lucide-react');
  
    const createCustomIcon = (assetStatus) => {
      const isBroken = assetStatus === 'BROKEN';
      const mainColor = isBroken ? 'bg-red-600' : 'bg-emerald-500';
      const pulseColor = isBroken ? 'bg-red-500' : 'bg-emerald-400';

      return L.divIcon({
        className: 'bg-transparent border-none', 
        html: `
          <div class="relative flex items-center justify-center w-12 h-12">
            <div class="absolute w-8 h-8 ${pulseColor} rounded-full animate-ping opacity-50"></div>
            <div class="relative z-10 flex items-center justify-center w-5 h-5 ${mainColor} rounded-full border-[3px] border-white shadow-[0_4px_10px_rgba(0,0,0,0.3)]"></div>
          </div>
        `,
        iconSize: [48, 48],
        iconAnchor: [24, 24], 
        popupAnchor: [0, -12] 
      });
    };

    return (
      <MapContainer center={center} zoom={18} style={{ height: '100%', width: '100%' }}>
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
  
          <LayersControl.BaseLayer name="Satellite View">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri...'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
        </LayersControl>
  
        <Marker
          draggable={true}
          eventHandlers={eventHandlers}
          position={center}
          ref={markerRef}
          icon={createCustomIcon(status)}
        >
          <Popup closeButton={false}>
            {/* 🎯 ربط الاتجاه (RTL/LTR) بناءً على لغة النظام */}
            <div className={`flex flex-col gap-2 min-w-[160px] p-1 font-sans ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? "rtl" : "ltr"}>
              <div className={`flex justify-between items-center border-b border-gray-200 pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="font-black text-[#1a3353] text-[13px] tracking-wider">{assetnum}</span>
                <span className={`w-2.5 h-2.5 rounded-full animate-pulse shadow-sm ${status === 'BROKEN' ? 'bg-red-600 shadow-red-500/50' : 'bg-green-500 shadow-green-500/50'}`}></span>
              </div>
              
              {/* 🎯 ترجمة الكلمات */}
              <p className="text-[10px] text-gray-500 font-bold uppercase">{t('assets.map.gps_location')}</p>
              
              {bim_id ? (
                <button 
                  onClick={() => onOpen3D(bim_id)}
                  className={`mt-1 bg-[#1a3353] hover:bg-blue-600 text-white text-[11px] font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition-all w-full shadow-md ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Globe size={14} /> {t('assets.bim.show_3d')}
                </button>
              ) : (
                <div className="mt-1 bg-gray-100 text-gray-400 text-[10px] font-bold py-1.5 px-3 rounded text-center border border-dashed border-gray-300">
                  {/* 🎯 ترجمة رسالة عدم وجود الموديل */}
                  {t('assets.bim.not_linked')}
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    );
  };

const AssetMaster = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('Asset');
  const [selectedSpareIdx, setSelectedSpareIdx] = useState(null);
  const [fieldMaps, setFieldMaps] = useState({});
  const [selectedMeterIdx, setSelectedMeterIdx] = useState(0);
  const [selectedSpecIdx, setSelectedSpecIdx] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showClassTree, setShowClassTree] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false); // إضافة هذا السطر
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isMeterModalOpen, setIsMeterModalOpen] = useState(false);
  const [isMeterHistoryOpen, setIsMeterHistoryOpen] = useState(false);
  const [isDowntimeOpen, setIsDowntimeOpen] = useState(false);
  const [isDowntimeHistoryOpen, setIsDowntimeHistoryOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isMoveHistoryOpen, setIsMoveHistoryOpen] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showIncidentHistory, setShowIncidentHistory] = useState(false);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [count, setCount] = useState(0);
  const [workHistory, setWorkHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [aiInsight, setAiInsight] = useState(null); // مخزن تحليل الـ AI
  const [loadingAi, setLoadingAi] = useState(false);
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const changeFontSize = fontContext?.changeFontSize || (() => {});
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const currency = session.currency || 'EGP';
  const [predictiveData, setPredictiveData] = useState(null);
  const [loadingPredictive, setLoadingPredictive] = useState(false);
  const [selectedPredMeter, setSelectedPredMeter] = useState(''); 
  const { canCreate, canUpdate, canDelete } = usePermissions('assets');
  const [activeBimGuid, setActiveBimGuid] = useState(null);
  const [liveStatuses, setLiveStatuses] = useState([]);

  const [asset, setAsset] = useState({
    asset_tag: '', assetnum: '', description: '', status: 'OPERATING', site_id: sessionSite, org_id: sessionOrg,
    location_id: null, parent_asset_id: null, classstructureid: '', priority: 0,
    assettype: '', isrunning: true, image_url: '', address: '',
    purchase_price: 0, replace_cost: 0, install_date: '', warranty_exp_date: '',
    vendor: '', po_num: '', model_urn: '', lat: 30.0444, lng: 31.2357, safetypnum: '',
    meters: [], spare_parts: [], specifications: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '' });
  const loadData = async () => {
      try {
          // جلب الـ Field Maps
          const [assetMaps, meterMaps] = await Promise.all([
              axios.get('/api/admin/field-map/assets'),
              axios.get('/api/admin/field-map/Meters')
          ]);
  
          setFieldMaps({
              ...(assetMaps.data || {}),
              ...(meterMaps.data || {}), 
          });
  
          // جلب بيانات الأصل
          if (id && id !== 'new') {
              const res = await axios.get(`/api/assets/${id}`);
              setAsset(res.data);
          }
      } catch (err) {
          console.error("Error loading asset data or mappings:", err);
          setFieldMaps({});
      }
  };
  
  // 2. الـ useEffect تناديها بس لما الـ id يتغير
  useEffect(() => {
      loadData();
  }, [id]);

  // =================================================================
  // 🎯 الاختيار التلقائي: عمل زووم على الأصل بمجرد فتح تابة الـ 3D
  // =================================================================
  useEffect(() => {
    if (activeTab === '3D View' && asset.bim_id) {
      // بنبعت التوقيت الجديد عشان نجبر مكون الـ 3D يشتغل حتى لو كنا فاتحينه قبل كده
      setActiveBimGuid(asset.bim_id, { timestamp: Date.now() });
    }
  }, [activeTab, asset.bim_id]);

  useEffect(() => {
    if (isNew && asset.org_id) {
        setAsset(prev => ({
          ...prev,
          asset_tag: '', assetnum: '', description: '', status: 'OPERATING', site_id: sessionSite, org_id: sessionOrg,
          location_id: null, parent_asset_id: null, classstructureid: '', priority: 0,
          assettype: '', isrunning: true, image_url: '', address: '',
          purchase_price: 0, replace_cost: 0, install_date: '', warranty_exp_date: '',
          vendor: '', po_num: '', model_urn: '', lat: 30.0444, lng: 31.2357, bim_id: '',
          meters: [], spare_parts: [], specifications: []         
          
        }));         
        axios.get(`/api/asset/next-number/${asset.org_id}`)
            .then(res => {
                setAsset(prev => ({ ...prev, assetnum: res.data.next_assetnum }));
            });
    }
  }, [isNew, asset.org_id]);

  useEffect(() => {
    // تعيين الحالة المبدئية للأصل الحالي
    if (asset.bim_id && asset.status) {
        setLiveStatuses([{ guid: asset.bim_id, status: asset.status }]);
    }

    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const ws = new WebSocket(`${protocol}${window.location.host}/ws/digital-twin`);

    ws.onopen = () => console.log("✅ تم الاتصال بنجاح! الديجيتال توين جاهز.");

    ws.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data);
            console.log("📥 إشارة لحظية مستلمة:", data);
            if (data.type === 'STATUS_CHANGE') {
                toast(`تحديث لحظي للتوأم الرقمي: ${data.status}`, { icon: '🔄', duration: 4000 });
                setLiveStatuses(prev => {
                    const filtered = prev.filter(item => item.guid !== data.guid);
                    return [...filtered, { guid: data.guid, status: data.status }];
                });
                setAsset(prevAsset => {
                    // بنتأكد إن الإشارة اللي جاية تخص الأصل اللي فاتحينه دلوقتي
                    if (prevAsset.bim_id === data.guid) {
                        console.log("🔄 جاري تحديث واجهة المستخدم للحالة الجديدة...");
                        return { 
                            ...prevAsset, 
                            status: data.status // بنغير الحالة فوق في الهيدر
                            // تقدر تحدث أي داتا تانية مبعوتة في الـ Payload هنا
                        };
                    }
                    return prevAsset;
                  });
            }
            // 🎯 السحر هنا: التأكد من إرسال الإشارة للـ BIMViewer
            if (data.type === 'TELEMETRY_UPDATE') {
                console.log("🚀 جاري إرسال أمر البابل للـ 3D...");
                // السطر ده هو اللي بيكلم الـ 3D Viewer ويقوله ارسم
                window.dispatchEvent(new CustomEvent('showAssetBubble', { detail: data }));
            }
        } catch (e) { console.error("WS Parse error", e); }
    };

    ws.onerror = (err) => console.error("❌ خطأ في اتصال الديجيتال توين:", err);
    ws.onclose = () => console.warn("⚠️ تم إغلاق خط الديجيتال توين.");

    // 🎯 التنظيف الذكي: نقفل الخط بس لو كان لسه بيحاول يفتح أو مفتوح فعلاً
    return () => {
        if (ws.readyState === WebSocket.OPEN) {
            ws.close();
        }
    };
  }, [asset.bim_id, asset.status]);

  const FontControls = () => {
    const { fontSize, changeFontSize } = useFontSize();
  
    return (
      <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => changeFontSize(-1)} className="hover:text-orange-400 transition-colors">
          <Minus size={14} />
        </button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center">
          <Type size={14} />
          <span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => changeFontSize(1)} className="hover:text-orange-400 transition-colors">
          <Plus size={14} />
        </button>
      </div>
    );
  };
  const handleShowInBIM = () => {
    if (asset.bim_id) {
      
      //setActiveBimGuid(asset.bim_id); // 🎯 تفعيل الزوم اللحظي
      setActiveTab('3D View');
    } else {
      toast.error("هذا الأصل غير مربوط بنموذج BIM");
    }
  };

  useEffect(() => {
    const handleBimClick = async (e) => {
      const { guid, name } = e.detail;
      // بحث في قاعدة البيانات عن الأصل اللي مربوط بالـ GUID ده
      toast.success(`تم اختيار الأصل: ${name} من الـ 3D`);
      // axios.get(`/api/assets/by-bim-guid/${guid}`).then(...)
    };
    window.addEventListener('bimElementClicked', handleBimClick);
    return () => window.removeEventListener('bimElementClicked', handleBimClick);
  }, []);
  
  const fetchPredictiveData = async () => {
    setLoadingPredictive(true);
    try {
        // شلنا الـ params من هنا لأن الباك إند هيسحب كل العدادات المربوطة بالأصل
        const res = await axios.get(`/api/assets/${asset.assetnum}/predictive-maintenance`);
        if (!res.data.error) {
            setPredictiveData(res.data);
        } else {
            setPredictiveData({ error: res.data.error });
        }
    } catch (err) {
        console.error("Failed to load predictive data", err);
        setPredictiveData({ error: "Failed to communicate with predictive engine." });
    } finally {
        setLoadingPredictive(false);
    }
  };

  // يشتغل لما التابة تفتح (شيلنا selectedPredMeter لأننا مش محتاجين Dropdown خلاص)
  useEffect(() => {
    if (activeTab === 'AI Analyze' && asset.assetnum) {
        fetchPredictiveData();
    }
  }, [activeTab, asset.assetnum]);

  const handleClassSelect = async (node) => {
    try {
      // 🎯 ترجمة رسالة جاري التحميل
      const loadId = toast.loading(t('workflow.loading')); 
      
      // جلب المواصفات الفنية المرتبطة بهذا التصنيف
      const res = await axios.get(`/api/classifications/${node.classstructureid}/attributes`);
      
      const newSpecs = res.data.map(attr => ({
        assetattrid: attr.assetattrid,
        attribute_name: attr.description,
        datatype: attr.datatype,
        uom: attr.uom,
        domainid: attr.domainid,
        attribute_value: '',
      }));
  
      setAsset(prev => ({
        ...prev,
        classstructureid: node.classstructureid,
        classstructureid_description: node.description,
        specifications: newSpecs // كدة الجدول اتملا لوحده!
      }));
  
      // 🎯 ترجمة رسالة النجاح
      toast.success(t('assets.toasts.save_success'), { id: loadId });
      setShowClassTree(false);
    } catch (err) {
      // 🎯 ترجمة رسالة الخطأ
      toast.error(t('assets.toasts.load_fail'));
    }
  };

  // دالة لجلب معلومات التنقل
  const loadNavigation = useCallback(async () => {
      if (!id || id === 'new') return;
      const searchParams = location.search; 
      try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`/api/assets/${id}/navigation${searchParams}`,{
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Nav load failed"); }
  }, [id]);
  
  useEffect(() => {
      loadNavigation();
  }, [loadNavigation]);
  
  // دالة التنقل الفعلي
  const goTo = (targetId) => {
      if (targetId) navigate(`/assets/${targetId}${location.search}`);
  };

  const handleSmartSearch = (fieldName, title, defaultType, onCustomSelect = null) => {
    let extraFilters = {};
    if (fieldName === 'itemnum' && defaultType === 'spareparts') {
      console.log("Applying extra filter for spare parts");
      extraFilters = { sparepart: true };
    }
    if (fieldName === 'failure_class' && defaultType === 'failureclasses') {
      console.log("Applying extra filter for failure classes");
      extraFilters = { org_id: asset.org_id }; // فلترة حسب منظمة الأصل الحالي
    }
    if (fieldName == 'vendor'){

    }
    if (fieldName === 'asst_category' && defaultType === 'assetcategories') {
      console.log("Applying extra filter for asset categories");
      extraFilters = { org_id: asset.org_id }; // فلترة حسب منظمة الأصل الحالي
    }
    if (fieldName === "location_id" && defaultType === "locations") {
      extraFilters = { org_id: asset.org_id, site_id: asset.site_id };
    }
    const linkedDomain = (fieldMaps || {})[fieldName];
    setLookup({ 
      isOpen: true, 
      title, 
      type: linkedDomain ? 'DOMAIN' : defaultType, 
      domainId: linkedDomain || '', 
      field: fieldName,
      filters: extraFilters,
      onCustomSelect: onCustomSelect // <--- ضيف السطر ده عشان السيستم يفتكر الدالة
    });
  };
  
  const handleSubSmartSearch = (fieldName, title, defaultType, tableName = 'Assets') => {
    // 1. ?? ????? ??? ?? ???? ???? (?? Meters)? ???? ????? ???? ????? ??? Mapping ?????
    // ???? ?????? ?? ????? ?? ??? fieldMaps ???????
    const linkedDomain = (fieldMaps || {})[fieldName];
  
    console.log(`Searching for: ${fieldName} in Table: ${tableName}, Found Domain: ${linkedDomain}`);
  
    setLookup({ 
      isOpen: true, 
      title: `Select Value: ${title}`, 
      type: linkedDomain ? 'DOMAIN' : defaultType, 
      domainId: linkedDomain || '', 
      field: fieldName,
      tableName: tableName // ???? ??? ?????? ???? ??? API ???? ?????
    });
  };
  
  const handleInputChange = (field, value) => {
    setAsset(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
        // 🎯 رسالة تأكيد مترجمة بنمط ماكسيمو
        const result = await Swal.fire({
            title: t('swal.delete_title'),
            text: `${t('swal.delete_text')} ${asset.assetnum}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('common.delete'),
            cancelButtonText: t('common.cancel'),
            customClass: {
                popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]`
            }
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/api/assets/${asset.asset_id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                // 🎯 توست النجاح المترجم
                toast.success(t('toasts.delete_success'));
                navigate('/assets'); // العودة للقائمة بعد الحذف
            } catch (err) {
                // 🎯 توست الفشل المترجم
                toast.error(`${t('toasts.save_fail')}: ${err.response?.data?.detail || "Unknown error"}`);
            }
        }
    };
    
  const fetchCount = useCallback(async () => {
    if (!asset.assetnum || isNew) {
        setCount(0);
        return;
    }
    try {
      const res = await axios.get(`/api/attachments/count/ASSET/${asset.assetnum}`);
      setCount(res.data.count || 0);
    } catch (err) { 
        console.error("Error fetching attachment count:", err); 
    }
  }, [asset.assetnum, isNew]);
  
  // جوه AssetMaster.js
const handleCategoryChange = async (categoryRow) => {
  const categoryId = categoryRow.category_id;
  try {
    const res = await axios.get(`/api/asset-categories-by-id/${categoryRow.id}`);
    const catData = res.data;

    // 🎯 توريث البيانات للأصل أوتوماتيك (Inheritance)
    setAsset(prev => ({
      ...prev,
      category_id: categoryId,
      depreciation_method: catData.depreciation_method,
      useful_life_years: catData.useful_life_years,
      salvage_value_percent: catData.salvage_value_percent
    }));
    
    // 🎯 توست النجاح المترجم مع دمج اسم الفئة
    toast.success(`${t('toasts.save_success')} - ${categoryId}`);
  } catch (err) {
    // 🎯 توست الفشل المترجم
    toast.error(t('toasts.load_fail'));
  }
};

const generatePerformancePDF = async () => {
    const input = document.getElementById('performance-tab-content');
    // تعريف الـ PDF بوضع Landscape (العرض)
    const pdf = new jsPDF('l', 'mm', 'a4'); 
    const lid = toast.loading("Generating Technical Report...");

    try {
        // الإعدادات الذهبية لضمان عدم ضغط الصورة
        const canvasOptions = {
            scale: 2, 
            useCORS: true,
            logging: false,
            windowWidth: 1400 // تثبيت العرض الوهمي لضمان توزيع العناصر بشكل صحيح
        };

        // 1. التقاط محتوى التابة
        const canvas = await html2canvas(input, canvasOptions);
        const imgData = canvas.toDataURL('image/png');
        
        // --- الصفحة الأولى (البيانات) ---
        const pageWidth = 297;  // عرض صفحة الـ A4 بالعرض
        const pageHeight = 210; // طول صفحة الـ A4 بالعرض

        // إضافة الهيدر الكحلي (بعرض الصفحة كاملة)
        pdf.setFillColor(26, 51, 83);
        pdf.rect(0, 0, pageWidth, 15, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Asset Reliability Report: ${asset.assetnum}`, 15, 10);
        
        // حساب الارتفاع المتناسب للصورة لتملأ عرض الصفحة
        const imgDisplayWidth = 287; // ترك 5 ملم هامش يمين وشمال
        const imgDisplayHeight = (canvas.height * imgDisplayWidth) / canvas.width;

        // وضع الصورة تحت الهيدر مع هامش بسيط
        pdf.addImage(imgData, 'PNG', 5, 20, imgDisplayWidth, imgDisplayHeight);

        // --- الصفحة الثانية (الملحق الفني) ---
        pdf.addPage('l', 'mm', 'a4');
        
        // خلفية الصفحة الثانية (رمادي فاتح جداً)
        pdf.setFillColor(248, 250, 252);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        
        pdf.setTextColor(26, 51, 83);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(18);
        pdf.text("Technical Appendix & Methodology", 15, 20);
        
        // خط فاصل شيك
        pdf.setDrawColor(234, 88, 12); // لون أورنج تكنويا
        pdf.setLineWidth(1);
        pdf.line(15, 24, 60, 24);

        // شرح المسميات بتنسيق أفضل
        const definitions = [
            { t: "Availability:", d: "Percentage of time the asset was functional vs. total possible time." },
            { t: "MTBF (Mean Time Between Failures):", d: "Measures reliability. Higher value indicates longer intervals between breakdowns." },
            { t: "MTTR (Mean Time To Repair):", d: "Measures maintenance efficiency. Lower value indicates faster recovery after failure." },
            { t: "OEE (Overall Equipment Effectiveness):", d: "Global standard combining Availability, Performance, and Quality." },
            { t: "RAV Index:", d: "Maintenance Cost as % of Asset Replacement Value. Target is usually < 3%." }
        ];

        let yPos = 45;
        definitions.forEach(item => {
            pdf.setFontSize(11);
            pdf.setTextColor(26, 51, 83);
            pdf.setFont("helvetica", "bold");
            pdf.text(item.t, 15, yPos);
            
            pdf.setFontSize(10);
            pdf.setTextColor(71, 85, 105);
            pdf.setFont("helvetica", "normal");
            pdf.text(item.d, 15, yPos + 6);
            yPos += 22;
        });

        // شرح المنهجية في بلوك منفصل
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(10, yPos + 10, 277, 40, 3, 3, 'FD');

        pdf.setTextColor(26, 51, 83);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text("Data Calculation Methodology:", 15, yPos + 20);
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 116, 139);
        const methodology = pdf.splitTextToSize(
            "This report is generated using real-time maintenance telemetry. MTBF and MTTR are calculated from Work Order 'COMP' timestamps and Downtime tracking modules. Statistical data is updated automatically in the AssetStats repository to ensure maximum data integrity and decision-making accuracy.", 260
        );
        pdf.text(methodology, 15, yPos + 28);

        // تذييل الصفحة (Footer)
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(`Teknora EAM System | Generated: ${new Date().toLocaleString()} | User: ${session.username}`, 15, 200);

        pdf.save(`Reliability_Report_${asset.assetnum}.pdf`);
        toast.success("Professional PDF Generated!", { id: lid });
    } catch (err) {
        toast.error("Failed to generate PDF", { id: lid });
        console.error(err);
    }
};

 const handleSave = async () => {
    // 🎯 رسالة "جاري الحفظ" مترجمة من قاموس العمليات
    const loadId = toast.loading(t('workflow.loading')); 
    try {
      // 🎯 1. جلب التوكن من التخزين المحلي
      const token = localStorage.getItem('token'); 

      // 🎯 2. إرسال الطلب مع الـ Authorization Header
      await axios.post('/api/assets/save', asset, {
        headers: {
          'Authorization': `Bearer ${token}` 
        }
      });

      // 🎯 توست النجاح المترجم
      toast.success(t('toasts.save_success'), { id: loadId });
      
      // التنقل للسجل الجديد/المحدث بنفس البارامترات
      navigate(`/assets/${asset.asset_id}${location.search}`, { replace: true });
      
      // إعادة تحميل البيانات لتحديث معلومات التدقيق (Audit Info)
      loadData(); 

    } catch (err) {
      // التعامل مع خطأ التوكن المنتهي أو الصلاحيات برسائل مترجمة
      if (err.response?.status === 401) {
        toast.error(t('toasts.load_fail'), { id: loadId }); // أو رسالة انتهاء الجلسة المخصصة
      } else {
        // 🎯 رسالة فشل الحفظ مع تفاصيل الخطأ التقني
        toast.error(`${t('toasts.save_fail')}: ${err.response?.data?.detail || "Unknown error"}`, { id: loadId });
      }
    }
  };

  useEffect(() => {
    const openStatusModal = () => {
      // فتح الـ Modal (يمكنك استخدام state للتحكم في ظهوره)
      setShowStatusModal(true);
    };
  
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, []);
  
  useEffect(() => {
    const openLogModal = () => {
      // فتح الـ Modal (يمكنك استخدام state للتحكم في ظهوره)
      setShowAudit(true);
    };
  
    window.addEventListener('GLOBAL_LOG_HANDLER', openLogModal);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLogModal);
  }, []);

  useEffect(() => {
    const openPrintModal = () => {
      setShowPrintOptions(true);
    };
  
    window.addEventListener('GLOBAL_PRINT_HANDLER', openPrintModal);
    return () => window.removeEventListener('GLOBAL_PRINT_HANDLER', openPrintModal);
  }, []);
  
  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);
  
  useEffect(() => {
    const openMeterReading = () => {
      // فتح الـ Modal (يمكنك استخدام state للتحكم في ظهوره)
      setIsMeterModalOpen(true);
    };
  
    window.addEventListener('GLOBAL_METER_READING_HANDLER', openMeterReading);
    return () => window.removeEventListener('GLOBAL_METER_READING_HANDLER', openMeterReading);
  }, []);
  
  useEffect(() => {
    // دالة الاستقبال
    const handleOpenHistory = () => {
      setShowHistoryModal(true);
    };
  
    // تشغيل المستمع
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
  
    // تنظيف المستمع عند إغلاق الشاشة عشان م يحصلش تكرار
    return () => {
      window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    };
  }, []);
  
  // ---  Event fot Meter History ---
  useEffect(() => {
    const handler = () => setIsMeterHistoryOpen(true);
    window.addEventListener('OPEN_METER_HISTORY', handler);
    return () => window.removeEventListener('OPEN_METER_HISTORY', handler);
  }, []);
  
  useEffect(() => {
      const handler = () => setIsDowntimeOpen(true);
      window.addEventListener('OPEN_DOWNTIME_MODAL', handler);
      return () => window.removeEventListener('OPEN_DOWNTIME_MODAL', handler);
  }, []);
  
  useEffect(() => {
      const handler = () => setIsDowntimeHistoryOpen(true);
      window.addEventListener('OPEN_DOWNTIME_HISTORY', handler);
      return () => window.removeEventListener('OPEN_DOWNTIME_HISTORY', handler);
  }, []);
  
  useEffect(() => {
      const handler = () => setIsMoveModalOpen(true);
      window.addEventListener('OPEN_MOVE_MODAL', handler);
      return () => window.removeEventListener('OPEN_MOVE_MODAL', handler);
  }, []);

  useEffect(() => {
      const handler = () => setIsMoveHistoryOpen(true);
      window.addEventListener('OPEN_MOVE_HISTORY', handler);
      return () => window.removeEventListener('OPEN_MOVE_HISTORY', handler);
  }, []);

  useEffect(() => {
      const handler = () => setShowIncidentModal(true);
      window.addEventListener('OPEN_INCIDENT_MODAL', handler);
      return () => window.removeEventListener('OPEN_INCIDENT_MODAL', handler);
  }, []);

  useEffect(() => {
      const handler = () => setShowIncidentHistory(true);
      window.addEventListener('OPEN_INCIDENT_HISTORY', handler);
      return () => window.removeEventListener('OPEN_INCIDENT_HISTORY', handler);
  }, []);
  
  useEffect(() => {
    const fetchWorkHistory = async () => {
if (!['Work History', '3D View'].includes(activeTab) || !asset.assetnum) return;
      
      setIsLoadingHistory(true);
      try {
        // نطلب من الباكيند أوامر الشغل الخاصة بهذا الأصل فقط
        const res = await axios.get(`/api/woforassets?assetnum=${asset.assetnum}`);
        setWorkHistory(res.data);
      } catch (err) {
        toast.error("Failed to load work history");
      } finally {
        setIsLoadingHistory(false);
      }
    };
    
    fetchWorkHistory();
  }, [activeTab, asset.assetnum]);
  
  // --- Upload Asset Image ---
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file (PNG, JPG)");
      return;
    }
  
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_num', asset.assetnum);
  
    const loadId = toast.loading("Uploading asset image...");
    try {
      // افترضنا وجود endpoint في الباكيند لرفع الصور
      const res = await axios.post('/api/assets/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAsset(prev => ({ ...prev, image_url: res.data.url }));
      toast.success("Image uploaded successfully", { id: loadId });
    } catch (err) {
      toast.error("Upload failed", { id: loadId });
    }
  };
  const fetchAiAnalysis2 = async () => {
      setLoadingAi(true);
      try {
          // بنكلم الباكيند اللي إنت لسه بانيه (Ollama Wrapper)
          const response = await axios.get(`https://ai.maqaree.com/api/analyze/${id}`);
          setAiInsight(response.data);
      } catch (err) {
          // 🎯 رسالة خطأ مترجمة من قاموس الـ AI
          toast.error(t('assets.toasts.load_fail')); 
      } finally {
          setLoadingAi(false);
      }
  };
  
  const fetchAiAnalysis = async () => {
    setLoadingAi(true);
    try {
        // 🎯 1. لازم نجلب إعدادات الذكاء الاصطناعي بتاعت المؤسسة دي الأول
        // عشان نعرف هنروح لسيرفر Local ولا Nvidia ونجيب الـ API Key
        const orgId = asset.org_id || asset.orgid;
        const orgRes = await axios.get(`/api/organizations/${orgId}`);
        const aiSettings = orgRes.data || {};
        
        const aiProvider = aiSettings.ai_provider || 'LOCAL';
        const apiKey = aiSettings.api_key || '';

        // 🎯 2. تحديد المسار (Endpoint) بناءً على الإعدادات
        let apiUrl = '';
        if (aiProvider === 'NVIDIA' && apiKey) {
            // توجيه للسيرفر السحابي مع إرسال المفتاح
            apiUrl = `https://ai.maqaree.com/api/analyze-cloud/${id}?api_key=${encodeURIComponent(apiKey)}`;
            console.log("🌐 Calling Groq Cloud AI...");
        } else {
            // توجيه للمحلي (Ollama)
            apiUrl = `https://ai.maqaree.com/api/analyze/${id}`;
            console.log("💻 Calling Local Ollama AI...");
        }

        // 3. جلب التحليل الأصلي (بالإنجليزي)
        const response = await axios.get(apiUrl);
        
        // 🛡️ حماية: التأكد إن الرد سليم ومش إيرور من الباك إند
        if (response.data.error) {
            console.error("Backend Error:", response.data.error);
            toast.error(`Error: ${response.data.error}`);
            setLoadingAi(false);
            return;
        }

        let aiData = response.data;
        console.log("1. Original AI Data (Raw):", aiData);

        // تنظيف كود اللغة
        let currentLanguage = localStorage.i18nextLng || 'en';
        if (currentLanguage.includes('-')) currentLanguage = currentLanguage.split('-')[0];

        // 4. الترجمة الفورية (توازي) لو اللغة مش إنجليزي
        if (currentLanguage !== 'en' && aiData.ai_insight) {
            
            const summaryText = aiData.ai_insight.condition_summary || "";
            const recText = aiData.ai_insight.ai_recommendation || ""; 

            const [summaryRes, recRes] = await Promise.all([
                axios.post('/api/translate', {
                    q: summaryText, source: 'en', target: currentLanguage, format: 'text'
                }),
                axios.post('/api/translate', {
                    q: recText, source: 'en', target: currentLanguage, format: 'text'
                })
            ]);

            let translatedSummary = summaryRes.data.translated_text || summaryRes.data.translatedText || summaryText;
            let translatedRec = recRes.data.translated_text || recRes.data.translatedText || recText;

            // تصحيح "هذيان" المترجم في العلامات الخاصة
            translatedSummary = translatedSummary.replace(/\*\*\s+/g, "**").replace(/\s+\*\*/g, "**");
            translatedRec = translatedRec.replace(/\*\*\s+/g, "**").replace(/\s+\*\*/g, "**");

            // حقن الترجمة جوه الـ Object
            aiData.ai_insight.condition_summary = translatedSummary;
            aiData.ai_insight.ai_recommendation = translatedRec;
        }

        console.log("4. Final Data to State:", aiData);
        setAiInsight(aiData);

    } catch (err) {
        console.error("Critical Analysis Error:", err);
        toast.error(t('assets.toasts.load_fail'));
    } finally {
        setLoadingAi(false);
    }
  };
  
  const fetchAiAnalysis_stable = async () => {
    setLoadingAi(true);
    try {
        // 1. جلب التحليل الأصلي (بالإنجليزي)
        const response = await axios.get(`https://ai.maqaree.com/api/analyze/${id}`);
        
        // 🛡️ حماية: التأكد إن الرد سليم ومش إيرور من الباك إند
        if (response.data.error) {
            console.error("Backend Error:", response.data.error);
            toast.error(`Error: ${response.data.error}`);
            setLoadingAi(false);
            return;
        }

        let aiData = response.data;
        console.log("1. Original AI Data (Raw):", aiData);

        // تنظيف كود اللغة
        let currentLanguage = localStorage.i18nextLng || 'en';
        if (currentLanguage.includes('-')) currentLanguage = currentLanguage.split('-')[0];

        // 2. الترجمة (لو اللغة مش إنجليزي)
        if (currentLanguage !== 'en' && aiData.ai_insight) {
            
            const summaryText = aiData.ai_insight.condition_summary || "";
            const recText = aiData.ai_insight.ai_recommendation || ""; // 🎯 استخراج التوصية كمان

            console.log("2. Texts to be translated:", { summaryText, recText });

            // 🎯 السحر هنا: نترجم الاتنين في نفس اللحظة (توازي) عشان نوفر وقت
            const [summaryRes, recRes] = await Promise.all([
                axios.post('/api/translate', {
                    q: summaryText, source: 'en', target: currentLanguage, format: 'text'
                }),
                axios.post('/api/translate', {
                    q: recText, source: 'en', target: currentLanguage, format: 'text'
                })
            ]);

            // استلام وتنظيف النصوص المترجمة
            let translatedSummary = summaryRes.data.translated_text || summaryRes.data.translatedText || summaryText;
            let translatedRec = recRes.data.translated_text || recRes.data.translatedText || recText;

            // 🎯 تصحيح "هذيان" المترجم في العلامات الخاصة (زي النجوم والمسافات)
            translatedSummary = translatedSummary.replace(/\*\*\s+/g, "**").replace(/\s+\*\*/g, "**");
            translatedRec = translatedRec.replace(/\*\*\s+/g, "**").replace(/\s+\*\*/g, "**");

            console.log("3. Cleaned Translations:", { translatedSummary, translatedRec });

            // 🎯 حقن الترجمة جوه الـ Object مباشرة (بدون JSON.parse القديم)
            aiData.ai_insight.condition_summary = translatedSummary;
            aiData.ai_insight.ai_recommendation = translatedRec;
        }

        console.log("4. Final Data to State:", aiData);
        setAiInsight(aiData);

    } catch (err) {
        console.error("Critical Analysis Error:", err);
        toast.error(t('assets.toasts.load_fail'));
    } finally {
        setLoadingAi(false);
    }
  };
  const fetchAiAnalysis1 = async () => {
    setLoadingAi(true);
    try {
        // 1. جلب التحليل الأصلي (بالإنجليزي)
        const response = await axios.get(`https://ai.maqaree.com/api/analyze/${id}`);
        let aiData = response.data;
        
        // 🔍 Log 1: رد سيرفر الـ AI الأصلي
        console.log("1. Original AI Data (Raw):", aiData);

        // تنظيف كود اللغة (من ar-EG لـ ar)
        let currentLanguage = localStorage.i18nextLng || 'en';
        if (currentLanguage.includes('-')) currentLanguage = currentLanguage.split('-')[0];

        // 2. هل اللغة الحالية ليست إنجليزية؟ إذن نحتاج ترجمة
        if (currentLanguage !== 'en') {
            const rawText = typeof aiData.ai_insight === 'string' 
                ? JSON.parse(aiData.ai_insight).condition_summary 
                : aiData.ai_insight.condition_summary;

            console.log("2. Text to be translated:", rawText);

            // 🎯 التعديل السحري: هننظف النص ونبعته للمترجم
            const translateRes = await axios.post('/api/translate', {
                q: rawText,
                source: 'en',
                target: currentLanguage,
                format: 'text'
            });

            // 🔍 التأكد من استلام النص المترجم
            let translatedText = translateRes.data.translated_text || translateRes.data.translatedText;

            console.log("3. Cleaned Translation:", translatedText);

            // 🎯 تصحيح "هذيان" المترجم في العلامات (النجوم)
            // المترجم أحياناً بيخليها ** نص ** إحنا هنرجعها لأصلها عشان الـ Split يشتغل
            translatedText = translatedText.replace(/\*\*\s+/g, "**").replace(/\s+\*\*/g, "**");

            if (typeof aiData.ai_insight === 'string') {
                let parsed = JSON.parse(aiData.ai_insight);
                parsed.condition_summary = translatedText;
                aiData.ai_insight = JSON.stringify(parsed);
            } else {
                aiData.ai_insight.condition_summary = translatedText;
            }
        }

        // 🔍 Log 3: البيانات النهائية قبل الـ SetState
        console.log("4. Final Data to State:", aiData);
        setAiInsight(aiData);

    } catch (err) {
        console.error("Critical Analysis Error:", err);
        toast.error(t('assets.toasts.load_fail'));
    } finally {
        setLoadingAi(false);
    }
};
  // ---  Revit URN ---
  const handleRevitUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_num', asset.assetnum);

    const loadId = toast.loading("Uploading & Translating Revit Model...");
    setIsUploading(true);

    try {
      const res = await axios.post('/api/bim/upload', formData);
      setAsset(prev => ({ ...prev, model_urn: res.data.urn }));
      toast.success("Model Linked Successfully!", { id: loadId });
    } catch (err) {
      toast.error("Upload Failed: " + err.message, { id: loadId });
    } finally {
      setIsUploading(false);
    }
  };
  
  const addNewSparePart = () => {
    const newPart = { 
      itemnum: '', 
      description: '', 
      quantity: 1.00, 
      issuedqty: 0.00, 
      remarks: '' 
    };
    
    // تحديث الـ Asset والـ Index في نفس الوقت لضمان التزامن
    setAsset(prev => {
      const currentParts = prev.spare_parts || [];
      const updatedParts = [...currentParts, newPart];
      // تحديث الـ Index لآخر سطر تم إضافته
      setSelectedSpareIdx(updatedParts.length - 1);
      return { ...prev, spare_parts: updatedParts };
    });
  };

  const addNewSpecification = () => {
    const newSpec = {
      assetattrid: '',
      attribute_name: '',
      //datatype: 'ALN', // النوع الافتراضي نصي
      attribute_value: '',
      //numvalue: null,
      uom: '',
      domainid: null
    };
    setAsset({
      ...asset,
      specifications: [...(asset.specifications || []), newSpec]
    });
    setSelectedSpecIdx((asset.specifications || []).length);
  };
    // دالة تحديث المواصفات
    const handleSpecUpdate = (field, value) => {
      if (selectedSpecIdx === null) return;
      const updatedSpecs = [...asset.specifications];
      updatedSpecs[selectedSpecIdx] = { ...updatedSpecs[selectedSpecIdx], [field]: value };
      setAsset({ ...asset, specifications: updatedSpecs });
    };
    
  // دالة التحديث
  const handleSpareUpdate = (field, value) => {
    const updatedParts = [...asset.spare_parts];
    updatedParts[selectedSpareIdx] = { ...updatedParts[selectedSpareIdx], [field]: value };
    setAsset({ ...asset, spare_parts: updatedParts });
  };
  const addNewMeter = () => {
    const newMeter = {
      //sequence: asset.meters.length + 1,
      meter_num : '',
      meter_name: '', meter_name: '', meter_type: 'CONTINUOUS',
      unit_of_measure: 'EACH', active: true, last_reading: 0, last_reading_date: '',
      remarks: ''
    };
    setAsset(prev => ({ ...prev, meters: [...prev.meters, newMeter] }));
    setSelectedMeterIdx(asset.meters.length);
  };

  const deleteMeter = (idx) => {
    const updatedMeters = asset.meters.filter((_, i) => i !== idx);
    setAsset(prev => ({ ...prev, meters: updatedMeters }));
    if (selectedMeterIdx >= updatedMeters.length) setSelectedMeterIdx(0);
  };

  const handleMeterUpdate = (fieldName, value) => {
    // ديبج داخلي للتأكد من الـ Index والبيانات
    console.log(`����️ Updating Field: ${fieldName} with Value: ${value} at Index: ${selectedMeterIdx}`);
  
    if (selectedMeterIdx === null || selectedMeterIdx === undefined) {
      console.error("❌ No Meter Index Selected!");
      return;
    }
  
    setAsset(prev => {
      // 1. نسخ مصفوفة العدادات
      const updatedMeters = [...(prev.meters || [])];
      
      // 2. تحديث السطر المحدد
      updatedMeters[selectedMeterIdx] = {
        ...updatedMeters[selectedMeterIdx],
        [fieldName]: value
      };
  
      // 3. إرجاع الـ Asset كامل مع المصفوفة الجديدة
      return { ...prev, meters: updatedMeters };
    });
  };
  useEffect(() => {
    fetchCount();
  }, [asset.assetnum, fetchCount]);

  // دالة جلب بيانات الأداء
const fetchPerformanceData = async () => {
  try {
    const res = await api.get(`/api/assets/${asset.assetnum}/performance`);
    setPerformance(res.data);
  } catch (err) {
    console.error("Failed to load performance metrics", err);
  }
};

useEffect(() => {
  if (activeTab === 'Evaluation') fetchPerformanceData();
}, [activeTab]);

  const currentMeter = asset.meters[selectedMeterIdx] || {};
    const TechnicalGauge = ({ value, min = 0, max = 100, label = "Temperature" }) => {
    // تحديد اللون بناءً على طلبك يابو التفانين
    const getColor = (val) => {
      if (val > 50) return '#ef4444'; // أحمر
      if (val >= 40) return '#f97316'; // برتقالي
      return '#22c55e'; // أخضر
    };

    const color = getColor(value);
    const percentage = Math.min(Math.max((value - min) / (max - min), 0), 1);
    const rotation = percentage * 180 - 90; // تحويل النسبة لزاوية دوران

    return (
      <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-slate-100 shadow-inner relative overflow-hidden h-full">
        <div className="relative w-48 h-24 overflow-hidden">
          {/* خلفية العداد (الرمادي) */}
          <div className="absolute w-48 h-48 border-[16px] border-slate-700 rounded-full"></div>
          
          {/* العداد الملون (الديناميكي) */}
          <div 
            className="absolute w-48 h-48 border-[16px] rounded-full transition-all duration-1000 ease-out"
            style={{ 
              borderColor: color,
              clipPath: 'inset(0 0 50% 0)',
              transform: `rotate(${rotation}deg)`,
              opacity: 0.8
            }}
          ></div>

          {/* المؤشر (الإبرة) */}
          <div 
            className="absolute bottom-0 left-1/2 w-1 h-20 bg-slate-800 origin-bottom -translate-x-1/2 transition-all duration-1000 ease-out shadow-lg"
            style={{ transform: `translateX(-50%) rotate(${rotation}deg)` }}
          >
            <div className="w-3 h-3 bg-slate-800 rounded-full absolute -bottom-1 -left-1"></div>
          </div>
        </div>

        {/* البيانات تحت العداد */}
        <div className="text-center mt-4">
          <p className="text-[10px] font-black text-slate-700 uppercase tracking-widest">{label}</p>
          <h4 className="text-3xl font-black italic" style={{ color }}>{value.toFixed(1)}°C</h4>
          <div className={`mt-1 px-3 py-0.5 rounded-full text-[8px] font-black uppercase text-white ${value > 50 ? 'bg-red-500' : value >= 40 ? 'bg-orange-500' : 'bg-green-500'}`}>
            {value > 50 ? 'Critical' : value >= 40 ? 'Warning' : 'Healthy'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] text-[0.8] overflow-hidden duration-300 transition-[font-size] ${isRTL ? 'Cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          console.log("✅ Row Selected from Lookup:", row);
          console.log("���� Is there a custom select function?", !!lookup.onCustomSelect);
          if(row.failure_class) {
            console.log("Selected Failure Class:", row.failure_class);
            setAsset(prev => ({ ...prev, failure_class: row.failure_class }));
            handleInputChange('failure_class', row.failure_class);
          }
          if(row.safetypnum){
            console.log("Selected Safety Plan:", row.safetypnum);
            setAsset(prev => ({ ...prev, safetypnum: row.safetypnum }));
            handleInputChange('safetypnum', row.safetypnum);
          }
          if(row.comp_num){
            setAsset(prev => ({...prev, vendor:row.comp_num}));
          }
      
          if (lookup.onCustomSelect) {
            console.log("���� Executing custom select...");
            lookup.onCustomSelect(row);
          } else {
            console.log("➡️ Executing default field update for:", lookup.field);
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />
      <IncidentModal 
        isOpen={showIncidentModal} 
        onClose={() => setShowIncidentModal(false)} 
        initialData={{
          assetnum: asset.assetnum,
          site_id: asset.site_id,
          org_id: asset.org_id
        }}
      />
      <IncidentHistoryModal 
        isOpen={showIncidentHistory} 
        onClose={() => setShowIncidentHistory(false)} 
        assetnum={asset.assetnum} 
        title={`Incident History for ${asset.assetnum}`}
      />

      {!isNew && (
      <AttachmentsModal 
          isOpen={isDocModalOpen} 
          onClose={() => {setIsDocModalOpen(false);fetchCount();}} 
          ownertable="ASSET" 
          ownerid={asset.assetnum} 
      />)}
      <EnterMeterReadings 
          isOpen={isMeterModalOpen} 
          onClose={() => setIsMeterModalOpen(false)} 
          assetNum={asset.assetnum} 
          meters={asset.meters || []} 
          //onRefresh={loadData} // لإعادة تحميل البيانات بعد الحفظ
      />
      {showStatusModal && (
        <StatusModal 
          currentStatus={asset.status}
          recordId={asset.assetnum}
          appName="ASSET" // إضافة اسم التطبيق هنا كـ Safety Net
          domainId={fieldMaps['status']} // لو ده undefined المودال هيصلحه داخلياً بفضل الكود الجديد
          onClose={() => setShowStatusModal(false)}
          onComplete={() => loadData()}
        />
      )}
      
      {showHistoryModal && (
        <StatusHistoryModal 
          appName="ASSET" // أو ASSET
          recordId={asset.assetnum} 
          onClose={() => setShowHistoryModal(false)} 
        />
      )}      
      
      <ClassificationTree 
        isOpen={showClassTree} 
        onClose={() => setShowClassTree(false)} 
        onSelect={handleClassSelect} 
      />
      
      <MeterHistoryModal 
          isOpen={isMeterHistoryOpen} 
          onClose={() => setIsMeterHistoryOpen(false)} 
          assetNum={asset.assetnum} 
      />
      
      <ReportDowntimeModal 
          isOpen={isDowntimeOpen} 
          onClose={() => setIsDowntimeOpen(false)} 
          assetNum={asset.assetnum}
          onRefresh={loadData}
      />
      
      <DowntimeHistoryModal 
          isOpen={isDowntimeHistoryOpen} 
          onClose={() => setIsDowntimeHistoryOpen(false)} 
          assetNum={asset.assetnum} 
      />
      
      <MoveAssetModal 
          isOpen={isMoveModalOpen} 
          onClose={() => setIsMoveModalOpen(false)} 
          asset={asset} // بنبعت كائن الأصل بالكامل عشان القيم الحالية تظهر
          onRefresh={loadData}
      />
      
      <MoveHistoryModal 
          isOpen={isMoveHistoryOpen} 
          onClose={() => setIsMoveHistoryOpen(false)} 
          assetNum={asset.assetnum} 
      />
      <AuditHistoryModal 
          isOpen={showAudit} 
          onClose={() => setShowAudit(false)} 
          tableName="assets" // 🎯 من الـ Wizard Domain
          recordId={asset.assetnum} 
      />
      
    {/* 1. TOP TOOLBAR (Control Center) */}
    <div className={`bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4`}>
      {/* جهة اليسار: الرجوع والنافيجيشن */}
      <div className={`flex items-center gap-3`}>
        <button onClick={() => navigate('/assets')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200" title={t('common.back')}>
          {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18} />}
        </button>
        
        {!isNew && (
          <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30">
                {isRTL ? <ChevronRight size={16} strokeWidth={3}/> : <ChevronLeft size={16} strokeWidth={3}/>}
            </button>
            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30">
                {isRTL ? <ChevronRight size={16}/> : <ChevronLeft size={16}/>}
            </button>
            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
              <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
            </div>
            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30">
                {isRTL ? <ChevronLeft size={16}/> : <ChevronRight size={16}/>}
            </button>
            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30">
                {isRTL ? <ChevronLeft size={16} strokeWidth={3}/> : <ChevronRight size={16} strokeWidth={3}/>}
            </button>
          </div>
        )}
      </div>

      {/* جهة اليمين: الأكشنز المجمعة */}
      <div className={`flex items-center gap-2`}>
        {((isNew && canCreate) || (!isNew && canUpdate)) && (
        <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md uppercase">
          <Save size={14} /> {t('common.save')}
        </button>
        )}
        <div className="h-6 w-[1px] bg-white/10 mx-1"></div>

        {/* زرار الطباعة الذكي */}
        <SmartPrintButton 
          moduleName="ASSET" 
          refId={asset.assetnum} 
          extraParams={{ asset_num: asset.assetnum, report_type: 'GL_SUMMARY' }} 
        />

        <button 
          onClick={() => setShowIncidentModal(true)} 
          className="p-2 hover:bg-orange-600/20 rounded-sm transition-all group" 
          title={t('assets.tabs.safety')}
        >
          <ShieldAlert size={16} className="text-orange-400 group-hover:scale-110 transition-transform" />
        </button>

        <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.change_status')}>
          <Tag size={16}/>
        </button>

        <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title={t('assets.tabs.attachments')}>
          <Paperclip size={16} />
          {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
        </button>

        <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200" title={t('assets.tabs.history')}>
          <History size={16}/>
        </button>

        {canDelete && (
        <button onClick={handleDelete} className="p-2 hover:bg-red-500/20 rounded text-red-400 transition-colors" title={t('common.delete')}>
          <Trash2 size={16}/>
        </button>
        )}

        <FontControls />
      </div>
    </div>

    {/* 2. ASSET SUMMARY HEADER (Slim Info Bar) */}
    <div className={`bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm z-20`}>
      <div className="flex flex-col min-w-[120px]" dir="ltr">
        <label className="text-[0.8] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.asset_num')}</label>
        <span className="text-sm font-black text-[#1a3353]">{asset.assetnum || 'NEW_ASSET'}</span>
      </div>

      <div className={`flex flex-col flex-1 border-l pl-8 ${isRTL ? 'border-r border-l-0 pr-8 pl-0 text-right' : ''}`}>
        <label className="text-[0.8] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.desc')}</label>
        <input 
          className="text-sm font-bold text-gray-700 outline-none focus:text-blue-600 bg-transparent w-full transition-all"
          value={asset.description}
          onChange={e => setAsset({...asset, description: e.target.value})}
          placeholder={t('assets.headers.desc') + "..."}
        />
      </div>
      
      <StaticField label={t('assets.headers.site')} value={asset.site_id}/>

      {asset.bim_id && (
        <button onClick={handleShowInBIM} className="flex items-center gap-1 text-blue-600 hover:text-blue-900 border-l pl-4">
          <Box size={14}/> {t('assets.tabs.bim')}
        </button>
      )}

      <div className={`flex flex-col min-w-[100px] border-l pl-8 ${isRTL ? 'border-r border-l-0 pr-8 pl-0' : ''}`}>
        <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.status')}</label>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full w-fit mt-1
          ${asset.status === 'OPERATING' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {asset.status}
        </span>
      </div>

      <div className={`flex flex-col min-w-[120px] border-l pl-8 ${isRTL ? 'border-r border-l-0 pr-8 pl-0' : ''}`}>
        <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.downtime')}</label>
        <div className="flex items-center gap-2 text-sm font-black text-orange-600">
            <Activity size={14}/> {Number(asset.total_down_time || 0).toFixed(2)} <small className="text-[8px]">{t('common.total')}</small>
        </div>
      </div>
    </div>

    {/* 3. MODERN TABS BAR */}
    <div className={`flex bg-white border-b border-gray-100 px-4 overflow-x-auto no-scrollbar shadow-inner`}>
      {[
        { id: 'Asset', label: t('assets.tabs.details'), icon: <Box size={13}/> },
        { id: 'Spare Parts', label: t('assets.tabs.spare_parts'), icon: <Plus size={13}/> },
        { id: 'Safety', label: t('assets.tabs.safety'), icon: <ShieldAlert size={13}/> },
        { id: 'Meters', label: t('assets.tabs.meters'), icon: <Activity size={13}/> },
        { id: 'Specifications', label: t('assets.tabs.specs'), icon: <FileText size={13}/> },
        { id: 'Work History', label: t('assets.tabs.history'), icon: <History size={13}/> },
        { id: 'Geomap', label: t('assets.tabs.map'), icon: <MapPin size={13}/> },
        { id: '3D View', label: t('assets.tabs.bim'), icon: <Globe size={13}/> },
        { id: 'AI Analyze', label: t('assets.tabs.ai'), icon: <BrainCircuit size={13} />},
        { id: 'Evaluation', label: t('assets.tabs.eval'), icon: <Activity size={13} />}
      ].map(tabItem => (
        <button 
          key={tabItem.id} 
          onClick={() => setActiveTab(tabItem.id)} 
          className={`px-6 py-3 flex items-center gap-2 transition-all whitespace-nowrap text-[10px] font-black uppercase tracking-tighter
          ${activeTab === tabItem.id 
            ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-blue-50/30' 
            : 'text-gray-400 hover:text-gray-600'}`}
        >
          {tabItem.icon} {tabItem.label}
        </button>
      ))}
    </div>

      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        
          {activeTab === 'Asset' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              {/* 🎯 ترجمة: معلومات عامة */}
              <SectionCard title={t('assets.sections.general')}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <DataField label={t('assets.headers.status')} value={asset.status}  />
                  <DataField label={t('assets.fields.type')} value={asset.assettype} onSearch={() => handleSmartSearch('assettype', t('assets.fields.type'), 'DOMAIN')} />
                  <EditableField label={t('assets.fields.location')} value={asset.location_id} onSearch={() => handleSmartSearch('location_id', t('assets.tabs.map'), 'locations')} lookupType="locations"/>
                  <DataField label={t('assets.fields.parent')} value={asset.parent} onSearch={() => handleSmartSearch('parent', t('assets.fields.parent'), 'assets')} />
                  <EditableField label={t('assets.fields.priority')} value={asset.priority} onChange={(val) => handleInputChange('priority', val)}/>
                  <EditableField 
                    label={t('assets.fields.category')} 
                    lookupType="assetcategories"
                    value={asset.category_id} 
                    onSearch={() => handleSmartSearch('category_id', t('assets.fields.category'), 'assetcategories', handleCategoryChange)} 
                  />
                  <StaticField label={t('assets.headers.downtime')} value={Number(asset.total_down_time || 0).toFixed(2)} />
                  <div className="flex items-center gap-2 pt-2">
                    <input 
                      type="checkbox" 
                      checked={asset.isrunning || false} 
                      onChange={(e) => handleInputChange('isrunning', e.target.checked)} 
                      className="w-4 h-4 rounded accent-blue-600 cursor-pointer" 
                    />
                    <span className="font-bold text-blue-900 uppercase">{t('assets.fields.is_running')}</span>
                  </div>
                </div>
              </SectionCard>

              {/* 🎯 ترجمة: تفاصيل إضافية */}
              <SectionCard title={t('assets.sections.details')}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <EditableField label={t('assets.fields.serial')} value={asset.serial} onChange={(val) => handleInputChange('serial', val)}/>
                  <EditableField label={t('assets.fields.item')} value={asset.itemnum} onSearch={() => handleSmartSearch('itemnum', t('assets.fields.item'), 'items')} lookupType="items" />
                  
                  <EditableField 
                    label={t('assets.fields.failure')} 
                    value={asset.failure_class} 
                    lookupType="failureclasses"
                    onSearch={() => handleSmartSearch(
                      'failure_class', 
                      t('assets.fields.failure'), 
                      'failureclasses', 
                      (selectedRow) => {
                        console.log("Custom Selection Triggered for Failure Class:", selectedRow);
                        handleInputChange('failure_class', selectedRow.failure_class);
                      }
                    )} 
                  />
                  <EditableField label={t('common.total') + " " + t('assets.fields.price')} value={asset.total_rate} readOnly />
                  <EditableField label={t('assets.fields.safety_plan')} value={asset.safetypnum} readOnly  lookupType="safetyplans"/>
                </div>
              </SectionCard>

              {/* 🎯 ترجمة: العناوين والبيانات المالية */}
              <SectionCard title={t('assets.sections.finance')}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <EditableField label={t('assets.fields.location')} value={asset.address} onChange={(val) => handleInputChange('address', val)}/>
                  <EditableField 
                    label={t('assets.fields.vendor')} 
                    lookupType="companies"
                    value={asset.vendor} 
                    onChange={(val) => handleInputChange('vendor', val)}
                    onSearch={() => handleSmartSearch('vendor', t('assets.fields.vendor'), 'companies', (row) => {
                        handleInputChange('vendor', row.comp_num || row.id);
                    })} 
                  />
                  <EditableField label={t('assets.fields.price')} value={asset.purchase_price ? Number(asset.purchase_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} onChange={(val) => handleInputChange('purchase_price', val)} desc={currency} />
                  <EditableField label={t('assets.fields.replace')} value={asset.replace_cost? Number(asset.replace_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} onChange={(val) => handleInputChange('replace_cost', val)} isHighlight desc={currency} />
                </div>
              </SectionCard>
            </div>

            <div className="col-span-4 space-y-6">
              {/* 🎯 ترجمة: صورة الأصل */}
              <SectionCard title={t('assets.sections.image')}>
                <div className="h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center relative overflow-hidden group">
                  {asset.image_url ? (
                    <>
                      <img src={asset.image_url} alt="Asset" className="h-full w-full object-contain p-2" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                         <Camera size={24} className="text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <Camera size={32} className="text-gray-300 mx-auto mb-2" />
                      <span className="text-gray-400 font-bold uppercase text-[9px]">{t('common.record')} ( {t('common.selected')} )</span>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="absolute inset-0 opacity-0 cursor-pointer" 
                    onChange={handleImageUpload}
                  />
                </div>
              </SectionCard>

              {/* 🎯 ترجمة: تكاليف السنة المالية */}
              <SectionCard title={t('assets.fields.ytd_cost')}>
                <div className="bg-blue-900 text-white p-4 rounded-lg shadow-inner">
                  <div className="flex items-center gap-2 mb-2 text-blue-300 uppercase text-[10px] font-black">
                    <DollarSign size={14}/> {t('assets.fields.ytd_cost')}
                  </div>
                  <div className="text-2xl font-black">
                    {Number(asset.ytdcost || 0).toLocaleString()} 
                    <small className="text-xs ml-1 opacity-50">{currency}</small>
                  </div>
                  <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-400" style={{width: '65%'}}></div>
                  </div>
                </div>
              </SectionCard>

              {/* 🎯 ترجمة: معلومات التدقيق */}
              <SectionCard title={t('assets.sections.audit')}>
                 <div className="text-[0.8] space-y-2 text-gray-500 font-bold">
                    <p className="flex justify-between uppercase">Modified By: <span className="text-blue-900">{asset.changeby}</span></p>
                    <p className="flex justify-between uppercase">Status Date: <span className="text-blue-700">{asset.changedate}</span></p>
                 </div>
              </SectionCard>

              {/* 🎯 ترجمة: بيانات النمذجة */}
              <SectionCard title={t('assets.sections.bim_info')}>
                  <div className="text-[0.8] space-y-2 text-gray-500 font-bold uppercase">
                      <p className="flex justify-between">BIM GUID: <span className="text-blue-900 font-mono">{asset.bim_id || 'NOT LINKED'}</span></p>
                  </div>
              </SectionCard>
            </div>
          </div>
        )}
        {activeTab === 'Spare Parts' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500">
            {/* 1. الجدول العلوي (The Master Table) */}
            <div className="bg-white border rounded shadow-sm overflow-hidden">
              <div className={`p-1 bg-gray-100 font-bold border-b text-[0.8] flex justify-between items-center px-3 text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span>{t('assets.tabs.spare_parts')}</span> 
                  <Filter size={12}/> 
                  {isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>} 
                  <span className="text-gray-400 font-normal">
                    {asset.spare_parts?.length > 0 ? `${selectedSpareIdx + 1} - ${asset.spare_parts.length}` : '0'} {t('common.of')} {asset.spare_parts?.length || 0}
                  </span>
                </div>
                <div className="flex gap-4">
                  <Download size={13} className="cursor-pointer hover:text-blue-600"/> 
                  <Maximize2 size={13} className="cursor-pointer hover:text-blue-600"/>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold text-[0.8]">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className={`p-2 border-r text-blue-700 underline ${isRTL ? 'text-right' : ''}`}>{t('assets.spare_fields.item')}</th>
                      <th className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{t('assets.headers.desc')}</th>
                      <th className="p-2 border-r text-right">{t('assets.spare_fields.qty')} ({t('common.total')})</th>
                      <th className="p-2 border-r text-right">{t('assets.spare_fields.issued')}</th>
                      <th className={`p-2 ${isRTL ? 'text-right' : ''}`}>{t('assets.spare_fields.remarks')}</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {asset.spare_parts?.map((sp, i) => (
                      <tr key={i} onClick={() => setSelectedSpareIdx(i)} 
                        className={`border-b hover:bg-blue-50 cursor-pointer text-[12px] ${selectedSpareIdx === i ? 'bg-[#e8f0fe]' : ''}`}>
                        <td className="p-2 text-center text-blue-600">
                          {selectedSpareIdx === i && (isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>)}
                        </td>
                        <td className={`p-2 border-r font-bold text-blue-900 ${isRTL ? 'text-right' : ''}`}>{sp.itemnum || '---'}</td>
                        <td className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{sp.description}</td>
                        <td className="p-2 border-r text-right">{parseFloat(sp.quantity || 0).toFixed(2)}</td>
                        <td className="p-2 border-r text-right">{parseFloat(sp.issuedqty || 0).toFixed(2)}</td>
                        <td className={`p-2 text-gray-500 truncate max-w-[150px] ${isRTL ? 'text-right' : ''}`}>{sp.remarks}</td>
                        <td className="p-2 text-center">
                          <Trash2 size={14} className="text-gray-300 hover:text-red-600 mx-auto" 
                            onClick={(e) => {e.stopPropagation(); /* Logic الحذف هنا */}}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          
            {/* 2. قسم التفاصيل (The Detail Section) */}
            {selectedSpareIdx !== null && asset.spare_parts?.[selectedSpareIdx] && (
              <div className="animate-in fade-in duration-300">
                <div className={`bg-gray-100 p-2 font-bold text-gray-600 border border-b-0 text-[0.8] ${isRTL ? 'text-right' : ''}`}>
                  {t('assets.spare_fields.item_details')}
                </div>
                <div className={`bg-white border p-4 grid grid-cols-2 gap-x-20 gap-y-4 shadow-sm ${isRTL ? 'text-right' : ''}`}>
                  <div className="space-y-4">
                    <EditableField 
                      label={`* ${t('assets.spare_fields.item')}`}
                      value={asset.spare_parts[selectedSpareIdx].itemnum} 
                      isRequired 
                      desc={asset.spare_parts[selectedSpareIdx].description}
                      onSearch={() => handleSmartSearch('itemnum', t('assets.fields.item'), 'items', (val) => {
                          handleSpareUpdate('itemnum', val.itemnum);
                          handleSpareUpdate('description', val.description);
                      })} 
                    />
                    <div className="flex flex-col gap-1">
                       <label className="text-gray-400 font-bold text-[11px] uppercase">{t('assets.spare_fields.qty')} ({t('common.total')})</label>
                       <input 
                         type="number" 
                         value={asset.spare_parts[selectedSpareIdx].quantity}
                         onChange={e => handleSpareUpdate('quantity', e.target.value)}
                         className={`border-b border-gray-300 outline-none py-1 focus:border-blue-600 text-[12px] ${isRTL ? 'text-right' : ''}`}
                       />
                    </div>
                  </div>
          
                  <div className="space-y-4">
                    <StaticField label={t('assets.spare_fields.issued')} value={parseFloat(asset.spare_parts[selectedSpareIdx].issuedqty || 0).toFixed(2)} />
                    <div className="flex flex-col gap-1">
                      <label className="text-gray-400 font-bold text-[11px] uppercase">{t('assets.spare_fields.remarks')}</label>
                      <textarea 
                        className={`border-b border-gray-300 outline-none h-16 resize-none text-[0.9] focus:border-blue-500 ${isRTL ? 'text-right' : ''}`}
                        value={asset.spare_parts[selectedSpareIdx].remarks}
                        onChange={e => handleSpareUpdate('remarks', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          
            {/* 3. الأزرار السفلى */}
            <div className={`flex gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button 
                onClick={() => {
                  if (selectedSpareIdx === null || (asset.spare_parts?.length === 0)) {
                    addNewSparePart();
                  }
                  handleSmartSearch('itemnum', t('assets.tabs.spare_parts'), 'spareparts', (selectedRow) => {
                    setAsset(prev => {
                      const updatedParts = [...(prev.spare_parts || [])];
                      const targetIdx = selectedSpareIdx ?? (updatedParts.length - 1);
                      if (targetIdx >= 0) {
                        updatedParts[targetIdx] = {
                          ...updatedParts[targetIdx],
                          itemnum: selectedRow.itemnum,
                          description: selectedRow.description || selectedRow.item_ref?.description || ''
                        };
                      }
                      return { ...prev, spare_parts: updatedParts };
                    });
                  });
                }}
                className="border border-[#3c70a4] text-[#3c70a4] px-6 py-1.5 rounded-sm font-bold hover:bg-blue-50 transition-all text-xs uppercase"
              >
                {t('common.record')} ( {t('common.selected')} )
              </button>
              <button 
                onClick={addNewSparePart}
                className="bg-[#3c70a4] text-white px-8 py-1.5 rounded-sm font-bold shadow hover:bg-[#1a3353] transition-all text-xs uppercase"
              >
                {t('common.record')} ( {t('common.total')} )
              </button>
            </div>
          </div>
        )}
        {activeTab === 'Safety' && (
          <div className={`max-w-7xl mx-auto space-y-6 animate-in slide-in-from-right duration-500 pb-20 ${isRTL ? 'text-right' : 'text-left'}`}>
            
            {/* 1. المرتبط بـ Safety Plans */}
            <SectionCard title={t('assets.safety.plan_card')}>
              <div className={`flex items-center justify-between bg-blue-50 p-4 border border-blue-100 rounded-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="p-3 bg-[#1a3353] text-white rounded-sm shadow-md">
                    <ShieldAlert size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{t('assets.safety.active_plan')}</p>
                    <p className="text-sm font-black text-[#1a3353]">{asset.safetypnum || t('assets.safety.no_plan')}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleSmartSearch('safetypnum', t('assets.safety.link_plan'), 'safetyplans', async (selectedPlan) => {
                    const planNum = selectedPlan.safetypnum;
                    try {
                      const loadId = toast.loading(t('assets.safety.applying_proto'));
                      setAsset(prev => ({ ...prev, safetypnum: planNum }));
                      const res = await axios.get(`/api/safety-plans/${planNum}`);
                      setAsset(prev => ({
                        ...prev,
                        safety_hazards: res.data.hazards || [],
                        loto_steps: res.data.loto || []
                      }));
                      toast.success(t('assets.toasts.save_success'), { id: loadId });
                    } catch (err) {
                      handleInputChange('safetypnum', planNum);
                      toast.error(t('assets.toasts.load_fail'));
                    }
                  })}
                  className="bg-white border border-[#1a3353] text-[#1a3353] px-4 py-1.5 rounded-sm font-black text-[10px] hover:bg-[#1a3353] hover:text-white transition-all uppercase"
                >
                  {asset.safetypnum ? t('assets.safety.change_plan') : t('assets.safety.link_plan')}
                </button>
              </div>
            </SectionCard>
        
            {/* 2. Hazards Table */}
            <SectionCard title={t('assets.safety.hazard_title')}>
              <div className="bg-white border rounded shadow-sm overflow-hidden">
                <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-[#f8fafc] border-b text-gray-700 text-[10px] font-black uppercase">
                    <tr>
                      <th className="p-3 border-r w-40">{t('assets.safety.hazard_num')}</th>
                      <th className="p-3 border-r">{t('assets.headers.desc')}</th>
                      <th className="p-3">{t('assets.safety.hazard_type')}</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {asset.safety_hazards?.length > 0 ? (
                      asset.safety_hazards.map((h, i) => (
                        <tr key={i} className="hover:bg-red-50/20 transition-colors">
                          <td className={`p-3 border-r font-black text-red-700 ${isRTL ? 'text-right' : ''}`}>{h.hazardnum}</td>
                          <td className={`p-3 border-r font-semibold text-gray-600 uppercase italic text-[11px] ${isRTL ? 'text-right' : ''}`}>{h.description}</td>
                          <td className="p-3">
                            <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-[9px] font-black uppercase">
                              {h.hazardtype || 'SAFETY'}
                            </span>
                          </td>
                          <td className="p-2">
                            <Trash2 size={14} className="text-gray-300 hover:text-red-600 cursor-pointer" />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-gray-300 italic">{t('assets.safety.no_hazards')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <button className={`m-3 text-[10px] font-black text-blue-700 uppercase flex items-center gap-1 hover:underline ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Plus size={12}/> {t('assets.safety.add_hazard')}
                </button>
              </div>
            </SectionCard>
        
            {/* 3. LOTO */}
            <SectionCard title={t('assets.safety.loto_title')}>
              <div className="bg-white border rounded shadow-sm overflow-hidden">
                <table className={`w-full text-[11px] ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-[#f8fafc] border-b text-gray-700 text-[10px] font-black uppercase">
                    <tr>
                      <th className="p-3 border-r w-32">{t('assets.safety.tag_id')}</th>
                      <th className="p-3 border-r">{t('assets.headers.desc')}</th>
                      <th className="p-3 border-r w-32">{t('assets.safety.req_state')}</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y font-bold uppercase">
                    {asset.loto_steps?.length > 0 ? (
                      asset.loto_steps.map((l, i) => (
                        <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                          <td className={`p-3 border-r text-blue-900 font-black ${isRTL ? 'text-right' : ''}`}>{l.tagoutnum}</td>
                          <td className={`p-3 border-r ${isRTL ? 'text-right' : ''}`}>
                            <p>{l.description}</p>
                            <p className={`text-[9px] text-gray-400 mt-1 flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <MapPin size={10}/> {l.location || 'At Asset'}
                            </p>
                          </td>
                          <td className="p-3 border-r text-center">
                            <span className="text-red-600 font-black">{l.required_state || 'OFF'}</span>
                          </td>
                          <td className="p-2">
                            <Trash2 size={14} className="text-gray-300 hover:text-red-600 cursor-pointer" />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-gray-300 italic">{t('assets.safety.no_loto')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <button className={`m-3 text-[10px] font-black text-blue-700 uppercase flex items-center gap-1 hover:underline ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Plus size={12}/> {t('assets.safety.add_loto')}
                </button>
              </div>
            </SectionCard>
          </div>
        )}
        {activeTab === 'Meters' && (
          <div className={`max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500 ${isRTL ? 'text-right' : 'text-left'}`}>
            {/* 1. الجدول العلوي (The Master Table) */}
            <div className="bg-white border rounded shadow-sm overflow-hidden">
              <div className={`p-1 bg-gray-100 font-bold border-b text-[0.8] flex justify-between items-center px-3 text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span>{t('assets.tabs.meters')}</span> 
                  <Filter size={12} className="cursor-pointer hover:text-blue-600"/> 
                  {isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>} 
                  <span className="text-gray-400 font-normal">
                    {asset.meters.length > 0 ? `${selectedMeterIdx + 1} - ${asset.meters.length}` : '0 - 0'} {t('common.of')} {asset.meters.length}
                  </span>
                </div>
                <div className="flex gap-4"><Download size={13}/> <Maximize2 size={13}/></div>
              </div>
              
              <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold text-[0.9]">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 border-r">{t('assets.meters.seq')}</th>
                    <th className="p-2 border-r text-blue-700 underline">{t('assets.meters.meter_name')}</th>
                    <th className="p-2 border-r">{t('assets.headers.desc')}</th>
                    <th className="p-2 border-r">{t('assets.meters.meter_type')}</th>
                    <th className="p-2 border-r">{t('assets.meters.uom')}</th>
                    <th className="p-2 text-center">{t('assets.meters.active_status')}</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {asset.meters.map((m, i) => (
                    <tr key={i} onClick={() => setSelectedMeterIdx(i)} 
                      className={`border-b hover:bg-blue-50 cursor-pointer text-[12px] ${selectedMeterIdx === i ? 'bg-[#e8f0fe]' : ''}`}>
                      <td className="p-2 text-center text-blue-600">
                        {selectedMeterIdx === i && (isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>)}
                      </td>
                      {/* 🎯 إصلاح السيكوينس: لو مش موجود في الداتا استخدم الـ Index + 1 */}
                      <td className="p-2 border-r text-gray-500">{m.sequence || i + 1}</td>
                      <td className="p-2 border-r font-bold text-blue-900">{m.meter_num || '---'}</td>
                      <td className="p-2 border-r">{m.meter_name}</td>
                      <td className="p-2 border-r">{m.meter_type}</td>
                      <td className="p-2 border-r">{m.unit_of_measure}</td>
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={m.active} readOnly className="accent-blue-600" />
                      </td>
                      <td className="p-2">
                        <Trash2 size={14} className="text-gray-300 hover:text-red-600" 
                          onClick={(e) => {e.stopPropagation(); deleteMeter(i);}}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        
            {/* 2. قسم التفاصيل (The Detail Section) */}
            {selectedMeterIdx !== null && asset.meters[selectedMeterIdx] && (
              <div className="animate-in fade-in duration-300">
                <div className={`bg-gray-100 p-2 font-bold text-gray-600 border border-b-0 text-[0.8] ${isRTL ? 'text-right' : ''}`}>
                  {t('assets.meters.details_title')} - {asset.meters[selectedMeterIdx].meter_num || t('assets.meters.new_meter')}
                </div>
                <div className={`bg-white border p-4 grid grid-cols-2 gap-x-20 gap-y-4 shadow-sm ${isRTL ? 'text-right' : ''}`}>
                  <div className="space-y-4">
                    <StaticField label={t('assets.meters.seq')} value={asset.meters[selectedMeterIdx].sequence || selectedMeterIdx + 1} />
                    
                    <EditableField 
                      label={`* ${t('assets.meters.meter_name')}`} 
                      value={asset.meters[selectedMeterIdx].meter_num} 
                      isRequired 
                      desc={asset.meters[selectedMeterIdx].meter_name}
                      onChange={v => handleMeterUpdate('meter_num', v)}
                      onSearch={() => handleSmartSearch('meter_id', t('assets.meters.meter_name'), 'meters',(selectedItem) => {
                        handleMeterUpdate('meter_num', selectedItem.meter_num);
                        handleMeterUpdate('meter_name', selectedItem.meter_name);
                        handleMeterUpdate('meter_type', selectedItem.meter_type);
                        handleMeterUpdate('unit_of_measure', selectedItem.unit_of_measure);
                      })} 
                    />
                    
                    <EditableField label={t('assets.meters.meter_type')} value={asset.meters[selectedMeterIdx].meter_type} onSearch={() => handleSmartSearch('meter_type', 'Domain', 'meters')} />
                    <EditableField 
                      label={t('assets.meters.uom')} 
                      value={asset.meters[selectedMeterIdx].unit_of_measure} 
                      onChange={v => handleMeterUpdate('unit_of_measure', v)}
                      onSearch={() => handleSubSmartSearch('unit_of_measure', 'uom', 'DOMAIN', 'Meters')}
                    />
                    
                    <div className={`flex items-center gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <input 
                        type="checkbox" 
                        id="meter_active"
                        checked={asset.meters[selectedMeterIdx].active} 
                        onChange={e => handleMeterUpdate('active', e.target.checked)} 
                        className="accent-blue-700 w-4 h-4 cursor-pointer" 
                      />
                      <label htmlFor="meter_active" className="font-bold text-gray-600 text-[12px] cursor-pointer">{t('assets.meters.active_status')}</label>
                    </div>
                  </div>
        
                  <div className="space-y-4">
                    <EditableField label={t('assets.meters.last_reading')} value={asset.meters[selectedMeterIdx].last_reading} readOnly />
                    <EditableField label={t('assets.meters.last_date')} hasCalendar type="datetime-local" value={asset.meters[selectedMeterIdx].last_reading_date} onChange={(val) => handleMeterUpdate('last_reading_date', val)} />
                    <EditableField label={t('assets.meters.inspector')} hasList value={asset.meters[selectedMeterIdx].inspector} />
                    <div className="flex flex-col gap-1">
                      <label className="text-gray-400 font-bold text-[12px]">{t('assets.meters.remarks')}</label>
                      <textarea 
                        className={`border-b border-gray-300 outline-none h-12 resize-none text-[0.9] focus:border-blue-500 transition-colors ${isRTL ? 'text-right' : ''}`}
                        value={asset.meters[selectedMeterIdx].remarks || ''}
                        onChange={e => handleMeterUpdate('remarks', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
        
            {/* 3. زر إضافة سطر جديد */}
            <div className={`pt-2 ${isRTL ? 'flex justify-end' : ''}`}>
              <button 
                onClick={() => {
                  addNewMeter();
                  setSelectedMeterIdx(asset.meters.length);
                }} 
                className="bg-[#3c70a4] text-white px-8 py-1.5 rounded-sm font-bold shadow hover:bg-[#1a3353] transition-all flex items-center gap-2 uppercase text-xs"
              >
                <span className="text-lg">+</span> {t('common.new_row')}
              </button>
            </div>
          </div>
        )}
        {activeTab === 'Specifications' && (
          <div className={`max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500 ${isRTL ? 'text-right' : 'text-left'}`}>
            
            {/* --- الهيدر الخاص بالتصنيف (Classification Header) --- */}
            <div className={`bg-white border p-4 shadow-sm rounded-sm flex flex-col md:flex-row justify-between items-end gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
              <div className="flex-1 w-full">
                <EditableField 
                  label={t('assets.specs.classification')} 
                  value={asset.classstructureid_description || asset.classstructureid} 
                  onSearch={() => setShowClassTree(true)}
                  placeholder={t('assets.specs.select_class')}
                />
              </div>
              <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button 
                  onClick={addNewSpecification}
                  className="bg-[#3c70a4] text-white px-6 py-1.5 rounded-sm font-bold shadow hover:bg-[#1a3353] transition-all text-xs flex items-center gap-2 uppercase"
                >
                  <Plus size={14} /> {t('common.new_row')}
                </button>
              </div>
            </div>

            {/* --- جدول المواصفات (The Master Table) --- */}
            <div className="bg-white border rounded shadow-sm overflow-hidden">
              <div className={`p-1 bg-gray-100 font-bold border-b text-[10px] flex justify-between items-center px-3 text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span>{t('assets.specs.tech_specs')}</span> 
                  <Filter size={12}/> 
                  {isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>} 
                  <span className="text-gray-400 font-normal">
                    {asset.specifications?.length > 0 ? `${selectedSpecIdx + 1} - ${asset.specifications.length}` : '0'} {t('common.of')} {asset.specifications?.length || 0}
                  </span>
                </div>
              </div>
              
              <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold text-[0.9]">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className={`p-2 border-r text-blue-700 underline ${isRTL ? 'text-right' : ''}`}>{t('assets.specs.attribute')}</th>
                    <th className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{t('assets.headers.desc')}</th>
                    <th className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{t('assets.specs.value')}</th>
                    <th className={`p-2 ${isRTL ? 'text-right' : ''}`}>{t('assets.specs.uom')}</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {asset.specifications?.map((spec, i) => (
                    <tr key={i} onClick={() => setSelectedSpecIdx(i)} 
                      className={`border-b hover:bg-blue-50 cursor-pointer text-[12px] ${selectedSpecIdx === i ? 'bg-[#e8f0fe]' : ''}`}>
                      <td className="p-2 text-center text-blue-600">
                        {selectedSpecIdx === i && (isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>)}
                      </td>
                      <td className={`p-2 border-r font-bold text-blue-900 ${isRTL ? 'text-right' : ''}`}>{spec.assetattrid || '---'}</td>
                      <td className={`p-2 border-r text-gray-500 ${isRTL ? 'text-right' : ''}`}>{spec.attribute_name || '---'}</td>
                      <td className={`p-2 border-r font-medium text-green-700 ${isRTL ? 'text-right' : ''}`}>
                        {spec.datatype === 'NUMERIC' ? spec.attribute_value : spec.attribute_value || '---'}
                      </td>
                      <td className={`p-2 text-gray-400 ${isRTL ? 'text-right' : ''}`}>{spec.uom || '---'}</td>
                      <td className="p-2">
                        <Trash2 size={14} className="text-gray-300 hover:text-red-600 mx-auto" onClick={(e) => e.stopPropagation()}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* --- تفاصيل السجل المختار (Attribute Details) --- */}
            {selectedSpecIdx !== null && asset.specifications?.[selectedSpecIdx] && (
              <div className="animate-in fade-in duration-300">
                <div className={`bg-gray-100 p-2 font-bold text-gray-600 border border-b-0 text-[0.9] uppercase ${isRTL ? 'text-right' : ''}`}>
                  {t('assets.specs.attr_details')}
                </div>
                <div className={`bg-white border p-4 grid grid-cols-2 gap-x-20 gap-y-4 shadow-sm ${isRTL ? 'text-right' : ''}`}>
                  <div className="space-y-4">
                    <EditableField 
                      label={t('assets.specs.attribute')} 
                      value={asset.specifications[selectedSpecIdx].assetattrid}
                      onSearch={() => handleSmartSearch('assetattrid', t('assets.specs.attribute'), 'attributes')}
                    />
                    <div className="flex flex-col gap-1">
                      <label className="text-gray-400 font-bold text-[11px] uppercase">{t('assets.specs.value')}</label>
                      <div className={`relative border-b border-gray-300 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <input 
                          className={`w-full outline-none py-1 focus:border-blue-600 text-[12px] ${isRTL ? 'text-right' : ''}`}
                          value={asset.specifications[selectedSpecIdx].attribute_value || ''}
                          onChange={e => handleSpecUpdate('attribute_value', e.target.value)}
                        />
                        {asset.specifications[selectedSpecIdx].domainid && (
                          <Search 
                            size={14} 
                            className="text-blue-600 cursor-pointer hover:scale-110 mx-2" 
                            onClick={() => handleSmartSearch('attribute_value', t('assets.specs.select_val'), 'DOMAIN', null, false, asset.specifications[selectedSpecIdx].domainid)}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <StaticField label={t('assets.specs.data_type')} value={asset.specifications[selectedSpecIdx].datatype} />
                    <StaticField label={t('assets.specs.uom')} value={asset.specifications[selectedSpecIdx].uom} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'Work History' && (
          <div className={`max-w-7xl mx-auto animate-in fade-in duration-500 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="bg-white border rounded shadow-sm overflow-hidden">
              <div className={`p-3 bg-gray-50 border-b flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="font-black text-blue-900 uppercase text-[0.9] tracking-widest">{t('assets.history.title')}</span>
                <div className="flex gap-2">
                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-[0.8] font-bold">
                    {t('common.total')}: {workHistory.length}
                  </span>
                </div>
              </div>
              <table className={`w-full text-left border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-slate-50 text-gray-400 font-bold text-[0.8] uppercase border-b">
                  <tr>
                    <th className="p-3 border-r">{t('assets.history.wo_num')}</th>
                    <th className="p-3 border-r">{t('assets.headers.desc')}</th>
                    <th className="p-3 border-r text-center">{t('assets.headers.status')}</th>
                    <th className="p-3 border-r text-center">{t('assets.history.wo_type')}</th>
                    <th className="p-3 text-center">{t('assets.history.finish_date')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y text-[11px]">
                  {isLoadingHistory ? (
                    <tr><td colSpan="5" className="p-10 text-center animate-pulse font-bold text-gray-400">{t('assets.history.fetching')}</td></tr>
                  ) : workHistory.length === 0 ? (
                    <tr><td colSpan="5" className="p-10 text-center text-gray-300 italic">{t('assets.history.no_history')}</td></tr>
                  ) : (
                    workHistory.map((wo, i) => (
                      <tr key={i} className="hover:bg-blue-50 cursor-pointer group" onClick={() => navigate(`/workorders/${wo.wonum}`)}>
                        <td className={`p-3 border-r font-black text-blue-900 group-hover:underline ${isRTL ? 'text-right' : ''}`}>{wo.wonum}</td>
                        <td className={`p-3 border-r text-gray-600 font-medium ${isRTL ? 'text-right' : ''}`}>{wo.description}</td>
                        <td className="p-3 border-r text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[0.8] font-black text-white 
                            ${wo.status === 'COMP' ? 'bg-green-600' : 'bg-blue-500'}`}>
                            {wo.status}
                          </span>
                        </td>
                        <td className="p-3 border-r text-center font-bold text-gray-400">{wo.wo_type}</td>
                        <td className="p-3 text-center font-mono text-gray-500">{wo.actfinish || '---'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}       
        {/* --- Tab 8: 3D View (Revit Linked & Digital Twin Enabled) --- */}
        {activeTab === '3D View' && (
          <div className="bg-slate-900 h-[650px] rounded-xl flex flex-col relative shadow-2xl border-4 border-slate-800 overflow-hidden">
            <div className={`absolute top-6 z-10 flex flex-col gap-3 ${isRTL ? 'right-6 items-end' : 'left-6 items-start'}`}>
              <div className={`bg-white/10 backdrop-blur-md p-3 rounded-lg border border-white/20 flex gap-4 shadow-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Globe className="text-blue-400" size={18} />
                <div className={`text-[10px] font-bold text-white uppercase ${isRTL ? 'text-right pl-4' : 'pr-4'}`}>
                  <p className="opacity-50">{t('assets.bim.model_status')}</p>
                  <p className={asset.model_urn ? "text-green-400" : "text-yellow-400"}>
                    {asset.model_urn ? t('assets.bim.linked') : t('assets.bim.not_linked')}
                  </p>
                </div>
                <div className={`border-white/10 flex items-center ${isRTL ? 'border-r pr-4' : 'border-l pl-4'}`}>
                  <label className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded cursor-pointer transition-all">
                    {isUploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                    <span className="font-bold text-[9px] uppercase tracking-tighter">{t('assets.bim.upload_revit')}</span>
                    <input type="file" accept=".rvt" onChange={handleRevitUpload} className="hidden" />
                  </label>
                </div>
              </div>
              
              {asset.model_urn && (
                <div className={`bg-black/40 backdrop-blur-sm p-2 rounded border border-white/5 text-[8px] text-white/50 font-mono break-all w-64 ${isRTL ? 'text-right' : 'text-left'}`}>
                  URN: {asset.model_urn}
                </div>
              )}
            </div>

            <div className="flex-1 w-full h-full p-2">
              <BIMViewer 
                urn={asset.model_urn} 
                activeGuid={activeBimGuid}
                assetStatuses={liveStatuses}
                assetData={asset}             // 👈 التعديل الأول: نبعت بيانات الأصل بالكامل
                workHistory={workHistory}     // 👈 التعديل التاني: نبعت تاريخ أوامر الشغل
                onAssetClick={(data) => {
                  toast.success(`Asset Selected: ${data.name}`);
                  // Future: if this is a master facility model, navigate to the selected asset
                  // axios.get(`/api/assets/by-bim-guid/${data.guid}`).then(res => navigate(`/assets/${res.data.assetnum}`));
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'Geomap' && (
          <div className="h-[500px] border-4 border-white rounded shadow-xl overflow-hidden relative">
            <MapView 
              lat={asset.lat} 
              lng={asset.lng} 
              assetnum={asset.assetnum} 
              bim_id={asset.bim_id}       
              status={asset.status}       
              t={t}             
              isRTL={isRTL}     
              onLocationChange={(newLat, newLng) => {
                setAsset(prev => ({ ...prev, lat: newLat, lng: newLng })); 
              }}
              onOpen3D={(bimId) => {      
                setActiveTab('3D View');  
                setActiveBimGuid(bimId);  
              }}
            />
            <div className={`absolute top-20 ${isRTL ? 'left-50' : 'right-50'} z-[1000] bg-white p-3 rounded shadow shadow-blue-200 border-l-4 border-blue-600`}>
               <p className="text-[9px] font-bold text-gray-400 uppercase">{t('assets.map.gps_coordinates', 'GPS Coordinates')}</p>
               <code className="text-blue-900 font-black">{asset.lat?.toFixed(4)}, {asset.lng?.toFixed(4)}</code>
            </div>
          </div>
        )}
        {activeTab === 'AI Analyze' && (
          <div className={`p-6 bg-[#f8fafc] min-h-screen animate-in fade-in duration-500 pb-20 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="max-w-6xl mx-auto space-y-6">
              
              {/* 1. Header Area */}
              <div className={`flex justify-between items-end border-b-2 border-slate-200 pb-4`}>
                <div className={isRTL ? 'text-right' : ''}>
                  <h3 className={`text-2xl font-black text-slate-800 flex items-center gap-3`}>
                    <BrainCircuit size={32} className="text-blue-600" /> 
                    {t('assets.ai.title')} <span className="text-blue-500 font-light text-sm italic">{t('assets.ai.sub_title')}</span>
                  </h3>
                  <p className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mt-1 italic">{t('assets.ai.tagline')}</p>
                </div>
                <button onClick={() => { fetchAiAnalysis(); fetchPredictiveData(); }} disabled={loadingAi || loadingPredictive}
                  className="bg-[#1a3353] text-white px-6 py-2 rounded shadow-xl font-black text-[10px] flex items-center gap-2 hover:bg-black transition-all active:scale-95">
                  <RefreshCw size={16} className={(loadingAi || loadingPredictive) ? "animate-spin" : ""} /> {t('assets.ai.run_btn')}
                </button>
              </div>

              {loadingAi ? (
                <div className="flex flex-col items-center justify-center py-40 gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <Brain size={40} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-600 animate-pulse" />
                  </div>
                  <p className="text-sm font-black text-slate-600 italic animate-bounce tracking-widest">{t('assets.ai.analyzing')}</p>
                </div>
              ) : (aiInsight && (aiInsight.ai_insight || aiInsight.condition_summary)) ? (() => {
                  
                  const data = typeof aiInsight.ai_insight === 'string' 
                    ? JSON.parse(aiInsight.ai_insight) 
                    : (aiInsight.ai_insight || aiInsight);
                    
                  const meters = aiInsight.source_data?.meters || [];
                  
                  return (
                    <div className="grid grid-cols-12 gap-6">
                      
                      {/* 2. KPI Cards */}
                      <div className="col-span-12 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <MASCard title={t('assets.ai.health_score')} value={`${data.health_score || 100}%`} status={(data.health_score || 100) > 80 ? 'success' : 'warning'} icon={<Activity size={20}/>} />
                        <MASCard title={t('assets.ai.thermal')} value={data.anomaly_detection?.thermal_anomaly ? t('assets.ai.critical') : t('assets.ai.normal')} status={data.anomaly_detection?.thermal_anomaly ? 'danger' : 'success'} icon={<ShieldAlert size={20}/>} />
                        <MASCard title={t('assets.ai.vibration')} value={data.anomaly_detection?.vibration_anomaly ? t('assets.ai.detected') : t('assets.ai.stable')} status={data.anomaly_detection?.vibration_anomaly ? 'danger' : 'success'} icon={<Activity size={20}/>} />
                        <MASCard title={t('assets.headers.status')} value={aiInsight.source_data?.status || 'UNKNOWN'} status={aiInsight.source_data?.status === 'OPERATING' ? 'success' : 'danger'} icon={<GitBranch size={20}/>} />
                      </div>

                      {/* 3. Detailed Summary Box */}
                      <div className="col-span-12 lg:col-span-7 space-y-6">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden h-full">
                          {(() => {
                            const rawText = data.condition_summary || "";
                            const sections = rawText.includes("**") 
                              ? rawText.split(/\*\*.*?\*\*/g).filter(s => s.trim().length > 5)
                              : [rawText, data.ai_recommendation || t('assets.ai.stable')];

                            return (
                              <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
                                <div className="bg-white border-2 border-slate-100 rounded-xl overflow-hidden shadow-sm">
                                  <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <thead>
                                      <tr className={`bg-slate-900 text-white uppercase text-[10px] tracking-widest`}>
                                        <th className="p-4 w-1/3">{t('ai_settings.section', 'Section')}</th>
                                        <th className="p-4">{t('ai_settings.technicalDetails', 'Technical Details')}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      <tr className="hover:bg-slate-50 transition-colors">
                                        <td className={`p-4 font-black text-blue-900 bg-slate-50/50 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                          <Activity size={16} className="text-blue-500" /> {t('assets.ai.tech_summary')}
                                        </td>
                                        <td className="p-4 text-[1.0em] leading-relaxed text-slate-600 font-medium">{sections[0] || "---"}</td>
                                      </tr>
                                      <tr className="hover:bg-slate-50 transition-colors">
                                        <td className={`p-4 font-black text-orange-600 bg-orange-50/20 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                          <Settings size={16} className="text-orange-500" /> {t('assets.ai.recommendations')}
                                        </td>
                                        <td className="p-4 text-[1.0em] leading-relaxed text-slate-700 font-bold italic">{sections[1] || data.recommended_action || "---"}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* 4. Sensor Comparison Chart */}
                      <div className="col-span-12 lg:col-span-5 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <h4 className={`text-[11px] font-black text-slate-400 uppercase mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Activity size={14}/> {t('assets.ai.sensors_chart')}
                        </h4>
                        <div className="h-[250px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={meters}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                              <XAxis dataKey="meter_name" tick={{fontSize: 10, fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                              <YAxis tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                              <Tooltip />
                              <Bar dataKey="last_reading" radius={[6, 6, 0, 0]} barSize={40}>
                                {meters.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.last_reading > 50 ? '#ef4444' : '#3b82f6'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* 🎯 [NEW] Combined Predictive Maintenance Chart */}
                        <div className="col-span-12 mt-2">
                          {loadingPredictive ? (
                              <div className="flex flex-col items-center justify-center p-10 bg-white rounded-xl border border-slate-200">
                                  <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mb-4"></div>
                                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('assets.ai.loading_pred')}</span>
                              </div>
                          ) : predictiveData && predictiveData.chart_data ? (
                              <PredictiveChart 
                                  data={predictiveData.chart_data} 
                                  metersSummary={predictiveData.meters_summary} 
                              />
                          ) : (
                              <div className="flex flex-col items-center justify-center p-10 text-slate-400 bg-white rounded-xl border border-slate-200">
                                  <Activity size={32} className="opacity-30 mb-2" />
                                  <p className="text-xs font-bold uppercase italic tracking-widest">{predictiveData?.error || "Not enough historical data."}</p>
                              </div>
                          )}
                        </div>

                    </div>
                  );
              })() : (
                <div className="text-center py-40 bg-white rounded-2xl border-4 border-dashed border-slate-100">
                  <Brain size={64} className="mx-auto text-slate-200 mb-4" />
                  <p className="text-slate-400 font-black italic uppercase tracking-tighter">{t('assets.ai.init_prompt')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'Evaluation' && (
          <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in zoom-in duration-500 pb-20">
              <div id="performance-tab-content" className="p-6 space-y-6 ...">
              
                  <div className="flex justify-end no-print">
                      <button 
                          onClick={generatePerformancePDF}
                          className="bg-[#1a3353] text-white px-4 py-2 rounded-sm font-black text-[10px] flex items-center gap-2 hover:bg-orange-600 transition-all shadow-lg"
                      >
                          <Download size={14} /> {t('export_technical_report')}
                      </button>
                  </div>

                  {/* صف الـ KPIs العلوية */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <KPICard 
                          title={t('availability')} 
                          value={performance?.availability || 100} 
                          unit="%" 
                          status={performance?.availability > 90 ? 'good' : performance?.availability > 75 ? 'average' : 'bad'}
                          icon={<Activity size={18} />}
                      />
                      <KPICard 
                          title={t('mtbf')} 
                          value={performance?.mtbf || 0} 
                          unit={t('hrs')} 
                          status={performance?.mtbf > 500 ? 'good' : 'average'}
                          icon={<ShieldAlert size={18} />}
                      />
                      <KPICard 
                          title={t('mttr')} 
                          value={performance?.mttr || 0} 
                          unit={t('hrs')} 
                          status={performance?.mttr < 4 ? 'good' : 'bad'}
                          icon={<Wrench size={18} />}
                      />
                      <KPICard 
                          title={t('reliability_score')} 
                          value={performance?.oee || 0} 
                          unit={t('score')} 
                          status={performance?.oee > 80 ? 'good' : 'average'}
                          icon={<BrainCircuit size={18} />}
                      />
                  </div>

                  <div className="grid grid-cols-12 gap-6">
                      {/* جراف تاريخ التوقفات */}
                      <div className="col-span-8 bg-white p-6 rounded-sm border shadow-sm">
                          <div className="flex justify-between items-center mb-6">
                              <h4 className="text-[11px] font-black text-blue-900 uppercase tracking-widest flex items-center gap-2">
                                  <BarChart size={16} className="text-orange-500" /> {t('downtime_history_trend')}
                              </h4>
                              <span className="text-[9px] text-gray-400 font-bold italic">{t('values_in_hours')}</span>
                          </div>
                          
                          <div className="h-[300px] w-full">
                              {performance?.history?.length > 0 ? (
                                  <ResponsiveContainer width="100%" height="100%">
                                      <BarChart 
                                          data={performance.history} 
                                          margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
                                      >
                                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                          
                                          <XAxis 
                                              dataKey="date" 
                                              tick={{fontSize: 9, fontWeight: 'bold'}} 
                                              axisLine={false} 
                                              tickLine={false}
                                              label={{ 
                                                  value: t('timeline_event_date'), 
                                                  position: 'insideBottom', 
                                                  offset: -20, 
                                                  fontSize: 13, 
                                                  fontWeight: 'bold', 
                                                  fill: '#64748b',
                                                  className: 'uppercase tracking-widest'
                                              }} 
                                          />

                                          <YAxis 
                                              tick={{fontSize: 9}} 
                                              axisLine={false} 
                                              tickLine={false}
                                              label={{ 
                                                  value: t('downtime_hours'), 
                                                  angle: -90, 
                                                  position: 'insideLeft', 
                                                  offset: 0, 
                                                  dy: 70,
                                                  fontSize: 13, 
                                                  fontWeight: 'bold', 
                                                  fill: '#64748b',
                                                  className: 'uppercase tracking-widest'
                                              }} 
                                          />

                                          <Tooltip 
                                              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '10px' }}
                                              cursor={{fill: '#f8fafc'}}
                                          />

                                          <Bar dataKey="duration" radius={[4, 4, 0, 0]} barSize={35}>
                                              {performance.history.map((entry, index) => (
                                                  <Cell key={`cell-${index}`} fill={entry.duration > 8 ? '#ef4444' : '#3b82f6'} />
                                              ))}
                                          </Bar>
                                      </BarChart>
                                  </ResponsiveContainer>
                              ) : (
                                  <div className="h-full flex flex-col items-center justify-center text-gray-300 italic gap-2">
                                      <Box size={40} className="opacity-20" />
                                      <p className="text-xs">{t('no_downtime_records')}</p>
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* الجانب الأيمن: التحليل الذكي */}
                      <div className="col-span-4 space-y-6">
                          <SectionCard title={t('reliability_analysis')}>
                              <div className="space-y-4 pt-2">
                                  <div className="flex justify-between items-center border-b pb-2">
                                      <span className="text-[10px] font-bold text-gray-500 uppercase">{t('total_failures')}</span>
                                      <span className="text-sm font-black text-red-600">{performance?.failure_count || 0}</span>
                                  </div>
                                  <div className="flex justify-between items-center border-b pb-2">
                                      <span className="text-[10px] font-bold text-gray-500 uppercase">{t('total_down_time')}</span>
                                      <span className="text-sm font-black text-[#1a3353]">{performance?.total_down_hours?.toFixed(1) || 0} <small className="text-[8px]">{t('hrs')}</small></span>
                                  </div>
                                  <div className="flex justify-between items-center border-b pb-2">
                                      <span className="text-[10px] font-bold text-gray-500 uppercase">{t('last_calculation')}</span>
                                      <span className="text-[12px] font-mono text-gray-700">{performance?.last_update ? new Date(performance.last_update).toLocaleString() : '---'}</span>
                                  </div>

                                  <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-600 rounded-r-md relative overflow-hidden group">
                                      <Brain size={40} className="absolute -right-2 -bottom-2 opacity-5 text-blue-900 group-hover:scale-125 transition-transform" />
                                      <p className="text-[9px] font-black text-blue-900 uppercase mb-2 flex items-center gap-1">
                                          <Brain size={12} /> {t('maintenance_strategy_note')}
                                      </p>
                                      <p className="text-[11px] leading-relaxed text-blue-800 italic font-medium">
                                          {performance?.mttr > 4 
                                              ? t('high_mttr_detected')
                                              : performance?.availability < 85 
                                              ? t('critical_availability')
                                              : t('nominal_performance')}
                                      </p>
                                  </div>
                              </div>
                          </SectionCard>

                          <div className="bg-white p-4 rounded-sm border shadow-sm flex items-center justify-between">
                              <div>
                                  <p className="text-[9px] font-black text-gray-400 uppercase">{t('current_load_factor')}</p>
                                  <p className="text-lg font-black text-green-600">{t('optimal')}</p>
                              </div>
                              <div className="w-12 h-12 rounded-full border-4 border-green-100 border-t-green-600 animate-spin-slow"></div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      )}
      {['Relationships'].includes(activeTab) && (
          <div className="p-20 text-center bg-white border-2 border-dashed border-gray-200 rounded-lg max-w-7xl mx-auto">
             <List size={40} className="mx-auto text-gray-200 mb-4" />
             <p className="text-gray-400 font-bold uppercase italic tracking-widest">Loading Data for {activeTab} Section...</p>
          </div>
        )}

      </div>
    </div>
  );
};

// --- Helpers ---

const KPICard = ({ title, value, unit, status, icon }) => {
    const statusColors = {
        good: 'text-green-600 border-green-500 bg-green-50/30',
        average: 'text-orange-500 border-orange-500 bg-orange-50/30',
        bad: 'text-red-600 border-red-500 bg-red-50/30'
    };

    return (
        <div className={`bg-white p-4 rounded-sm border shadow-sm border-l-4 ${statusColors[status].split(' ')[1]} transition-all hover:translate-y-[-2px]`}>
            <div className="flex justify-between items-start mb-1">
                <div className="text-gray-400">{icon}</div>
                <div className={`w-2 h-2 rounded-full ${statusColors[status].split(' ')[0].replace('text', 'bg')} animate-pulse`}></div>
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">{title}</p>
            <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-2xl font-black italic ${statusColors[status].split(' ')[0]}`}>
                    {typeof value === 'number' ? value.toFixed(1) : value}
                </span>
                <span className="text-[9px] font-bold text-gray-400">{unit}</span>
            </div>
        </div>
    );
};

const MASCard = ({ title, value, status, icon }) => {
  const styles = {
    success: 'bg-green-50 border-green-200 text-green-700 shadow-green-100',
    warning: 'bg-orange-50 border-orange-200 text-orange-700 shadow-orange-100',
    danger: 'bg-red-50 border-red-200 text-red-700 shadow-red-100',
  };

  const dotColors = {
    success: 'bg-green-500',
    warning: 'bg-orange-500',
    danger: 'bg-red-500',
  };

  return (
    <div className={`p-4 rounded-xl border-2 shadow-lg transition-all hover:scale-105 ${styles[status]}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="p-2 bg-white/50 rounded-lg">{icon}</div>
        <div className={`w-3 h-3 rounded-full animate-pulse ${dotColors[status]}`}></div>
      </div>
      <p className="text-[9px] font-black uppercase opacity-60 tracking-tighter">{title}</p>
      <h5 className="text-xl font-black mt-1 uppercase italic">{value || 'N/A'}</h5>
    </div>
  );
};

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col">
    <label className="text-[0.8] text-gray-400 font-bold uppercase tracking-widest">{label}</label>
    <input className={`${width} border-b border-gray-300 outline-none py-1 font-bold uppercase ${isBold ? 'text-blue-900 text-sm' : 'text-blue-700'} bg-transparent transition-all focus:border-blue-500`} 
           value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}/>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm">
    <div className="p-2 bg-slate-50 border-b font-bold text-slate-500 uppercase text-[0.8] tracking-widest flex items-center gap-2 shadow-inner">
      <div className="w-1 h-3 bg-blue-500 rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const DataField = ({ label, value, onSearch, onChange, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5 group hover:bg-blue-50/30 transition-all rounded px-1">
    <label className="text-[0.8] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-center h-5">
      <span className={`font-bold uppercase truncate ${isHighlight ? 'text-green-700 text-sm' : 'text-blue-900'}`}>{value || ''}</span>
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
    </div>
  </div>
);

const SearchField = ({ label, value, onSearch, onChange, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5 group hover:bg-blue-50/30 transition-all rounded px-1">
    <label className="text-[0.8] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-center h-5">
      <input 
        readOnly={!onChange}
        className={`font-bold uppercase truncate bg-transparent outline-none w-full ${isHighlight ? 'text-green-700 text-sm' : 'text-blue-900'}`}
        value={value || ''} 
        onChange={(e) => onChange && onChange(e.target.value)}
      />
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
    </div>
  </div>
);

const StaticField = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 pb-1">
    <label className="text-gray-400 font-bold text-[0.8] uppercase tracking-tighter">{label}</label>
    <div className="font-bold text-gray-700 min-h-[15px]">{value || ''}</div>
  </div>
);

const EditableField2 = ({ label, value, onChange, onSearch, isRequired, lookupType, hasList, hasCalendar, desc, type = "text" }) => {
  const [description, setDesc] = useState("");

  // 🎯 السحر هنا: أول ما قيمة الكود تتغير، بنروح نجيب الوصف فوراً
  useEffect(() => {
    if (value && lookupType) {
      const fetchDesc = async () => {
        try {
          const res = await axios.get(`/api/lookup-desc/resolve?type=${lookupType}&code=${value}`);
          setDesc(res.data.description);
        } catch (err) {
          setDesc("");
        }
      };
      fetchDesc();
    } else {
      setDesc("");
    }
  }, [value, lookupType]);
  return(
  <div className="flex flex-col gap-1 border-b border-gray-300 group">
    <label className={`${isRequired ? 'text-red-800' : 'text-gray-400'} font-bold text-[0.8] uppercase tracking-tighter`}>{label}</label>
    <div className="flex items-center justify-between pb-1">
      <input 
        type={type} // <--- غيرنا دي عشان تستقبل النوع (text, datetime-local, number, etc.)
        className="outline-none font-bold text-blue-900 bg-transparent w-full custom-datetime-input" 
        value={type === "datetime-local" ? value.substring(0, 16).replace(' ', 'T') : value || ''} 
        onChange={e => onChange && onChange(e.target.value)} 
      />
      {desc && <span className="text-gray-400 font-normal px-4 italic whitespace-nowrap">{desc || description}</span>}
      <div className="flex gap-2 text-blue-700 cursor-pointer">
        {hasList && <List size={14}/>}
        {hasCalendar && <Calendar size={14}/>}
        {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors" onClick={onSearch} />}
      </div>
    </div>
  </div>
)};

const EditableField = ({ label, value, onChange, onSearch, isRequired, lookupType, hasList, hasCalendar, desc, type = "text" }) => {
  const [description, setDesc] = useState("");
  // 🎯 استيت محلي عشان اليوزر يكتب براحته بدون تدخل التنسيق المالي أثناء الكتابة
  const [inputValue, setInputValue] = useState(value || "");

  // تحديث الاستيت المحلي لو الـ value اللي جاية من بره اتغيرت (عشان الـ Load)
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // منطق جلب الوصف (Lookup Description)
  useEffect(() => {
    if (value && lookupType) {
      const fetchDesc = async () => {
        try {
          const res = await axios.get(`/api/lookup-desc/resolve?type=${lookupType}&code=${value}`);
          setDesc(res.data.description || "");
        } catch (err) { setDesc(""); }
      };
      fetchDesc();
    } else { setDesc(""); }
  }, [value, lookupType]);

  // دالة التنسيق المالي (بتشتغل فقط عند العرض)
  const formatCurrency = (val) => {
    if (val === "" || val === null || isNaN(val)) return val;
    return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // هل الحقل ده مالي؟ (Price أو Cost)
  const isNumericField = label.toLowerCase().includes("price") || label.toLowerCase().includes("cost") || label.toLowerCase().includes("rate");

  return (
    <div className="flex flex-col gap-1 border-b border-gray-300 group mb-2">
      <label className={`${isRequired ? 'text-red-800' : 'text-gray-400'} font-bold text-[0.9] uppercase tracking-tighter`}>
        {label}
      </label>
      
      <div className="flex items-center justify-between pb-0.5">
        <input 
          type={type === "datetime-local" ? "datetime-local" : "text"} 
          className="outline-none font-black text-blue-900 bg-transparent w-full text-xs" 
          // 🎯 لو حقل مالي، اعرض القيمة الخام أثناء الكتابة، والمعادلة (الفواصل) وأنت بره
          value={type === "datetime-local" && inputValue ? inputValue.substring(0, 16).replace(' ', 'T') : inputValue}
          
          onChange={e => {
            let val = e.target.value;
            if (isNumericField) {
              // السماح فقط بالأرقام والنقطة العشرية (تنظيف أي فواصل جاية بالغط)
              val = val.replace(/,/g, '');
              if (val !== "" && isNaN(val) && val !== ".") return;
            }
            setInputValue(val); // تحديث المنظر قدام اليوزر فوراً
            if (!isNumericField) onChange(val); // لو مش مالي سيف فوراً
          }}

          onBlur={() => {
            // 🎯 لما اليوزر يخرج من الخانة، نبعت القيمة النهائية للـ Parent وننظفها
            if (isNumericField) {
              const cleanVal = String(inputValue).replace(/,/g, '');
              onChange(cleanVal);
              // نحول المنظر للفواصل بعد الخروج
              setInputValue(formatCurrency(cleanVal));
            }
          }}

          onFocus={() => {
            // 🎯 لما يرجع يضغط، نشيل الفواصل عشان يعرف يمسح ويعدل بسهولة
            if (isNumericField) {
              setInputValue(String(inputValue).replace(/,/g, ''));
            }
          }}
        />
        
        <div className="flex gap-2 text-blue-700 cursor-pointer shrink-0">
          {hasList && <List size={14}/>}
          {hasCalendar && <Calendar size={14}/>}
          {onSearch && <Search size={13} className="text-blue-400 hover:text-blue-800 transition-colors" onClick={onSearch} />}
        </div>
      </div>

      {(description || desc) && (
        <div className="text-[10px] text-orange-600 font-bold italic animate-in fade-in slide-in-from-left-1 leading-none pb-1">
          {description || desc}
        </div>
      )}
    </div>
  );
};

export default AssetMaster;